import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { Guid } from 'typescript-guid';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { IONote } from '../../classes/io/io-note.class';
import {
  SampleDeliveryRequest,
  SdrCustomerAllocation,
  SdrProductLine,
  SDR_TYPE_DELIVERY_REQUEST,
  SdrStatus,
} from '../../classes/sample/sample-delivery-request.class';
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME } from '@omni/services/disk/disk.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { DB_KEY_PREFIXES, DB_ALLDOCS_QUERY_OPTIONS, DB_SYNC_STATE_KEYS } from '@omni/config/pouch-db.config';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { Utility } from '@omni/utility/util';
import { TranslateService } from '@ngx-translate/core';
import { MyAssistantService, NOTIFICATION } from '@omni/services/my-assistant/my-assistant.service';
import { IndNotificationDataModel } from '@omni/models/indNotificationDataModel';

const SDR_ENTITY = 'indskr_samplerequests';
const ANNOTATIONS_ENTITY = 'annotations';
// Sample Line Item collection (indskr_samplereconciliation, plural OData set
// name) — each product line on an SDR is created as its OWN independent
// request against this collection (see createSdr's per-line comment), not
// deep-inserted under the header.
const LINE_ITEM_ENTITY = 'indskr_samplereconciliations';
// Allocation Config collection (indskr_sampleproductdropconfiguration, plural
// OData set name) — fetchManualAllocationsForContact is now rooted here (not
// on Customer Allocation), outer-joined to Customer Allocation, so a row
// comes back even on a customer's first-ever request for a SKU. See the
// FetchXML's own comment.
const ALLOCATION_CONFIG_ENTITY = 'indskr_sampleproductdropconfigurations';
// Customer Allocation collection (indskr_customersampleproduct, plural OData
// set name) — root entity for fetchManualAllocationsDirtyContactsForPosition.
// Confirmed against live UAT entity metadata (EntityDefinitions?$select=
// EntitySetName), not just assumed pluralization.
const CUSTOMER_SAMPLE_PRODUCT_ENTITY = 'indskr_customersampleproducts';

// Application-level timeout (ms) applied to EACH standalone create call in
// createSdr() (header, every line item, every note). DynamicsWebApi otherwise
// sets no XHR timeout, so a connection that drops or stalls mid-flight
// (Issue 2) or never completes on a degraded network (Issue 3) leaves the XHR
// hanging until the OS-level TCP timeout (60–120s). While it hangs, submit()'s
// `finally` never runs: `isSubmitting` stays true, the Submit button stays
// greyed out, and no error toast fires. A bounded timeout guarantees the
// promise rejects, so `finally` clears `isSubmitting`, the gate permit is
// released, and the caller shows a retry-friendly toast.
// 60s, not less: manual testing on slow 3G with an SDR carrying 5–10 note
// attachments showed a comparable batch call did NOT complete inside 30–40s —
// at those shorter ceilings a legitimate submit was still in flight and got
// killed. 60s gives those real uploads room to finish, while still bounding
// the wait well inside the uncontrolled 60–120s OS TCP timeout. Reconcile
// guards any retry against duplicating a submit that did land.
const SDR_SUBMIT_TIMEOUT_MS = 60000;

// indskr_samplerequest's statecode enum (indskr_SampleRequestState) has two
// members, Active (0) and Inactive (1) — the Dynamics default for a simple
// two-state custom entity. Every SdrStatus.* value except Inactive itself
// (Active/Draft, Submitted, SentTo3PL, Delivered, Signed) is a "reason" that
// lives under statecode Active; Dynamics' StatusCodeValidatorStep rejects a
// statuscode belonging to a DIFFERENT statecode than the one currently set on
// the record (confirmed live: PATCHing statuscode=Inactive alone, with
// statecode left at Active, throws "2 is not a valid status code for state
// code indskr_SampleRequestState.Active"). scrapSdr/uploadPendingSdrScraps
// must always set BOTH fields together, same as Sample Drop's own scrap
// (statecode=2/statuscode=3 on indskr_sampledrop).
const SDR_INACTIVE_STATECODE = 1;

// Origin marker stamped on indskr_externalid. Full value is
// `${SDR_EXTERNAL_ID_PREFIX}${sdrId}`: prefix denotes GoApp origin, the
// client-generated sdrId makes it unique and deterministic, so a reconcile
// retry (same sdrId) reproduces the same externalid — upsert/alternate-key safe.
const SDR_EXTERNAL_ID_PREFIX = 'GoApp_';

// @odata.bind nav property for the created-by user lookup (indskr_user →
// systemuser). Single-target lookup, so the bare schema name is expected;
// centralised so backend smoke can flip it to a target-suffixed form
// (indskr_User_systemuser) in one place. Verify against entity metadata.
const SDR_USER_NAV = 'indskr_User@odata.bind';

// @odata.bind nav property for the appointment lookup (indskr_samplerequest ->
// appointment). The field IS a real lookup (confirmed) — the capitalized guess
// `indskr_Appointment@odata.bind` (matching the SDR_USER_NAV/customer-bind
// pattern below) failed live with "undeclared property 'indskr_Appointment'"
// (0x80048d19: the whole batch/changeset rejects on an unrecognized @odata.bind
// property name — Dynamics does NOT silently ignore it). Trying the lowercase
// form matching the logical name (`_indskr_appointment_value` on the class,
// same attribute name the fetchByAppointment query uses) instead. VERIFY
// against entity metadata (ManyToOneRelationships) if this also fails.
const SDR_APPOINTMENT_NAV = 'indskr_appointment@odata.bind';

export interface StagedSdrNote {
  noteText: string;
  attachment?: {
    documentBody: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
  };
  /** Stable client-generated id, stamped once when the note is first staged
   *  (save-on-click add). Lets removeSdrNoteOffline target this exact note
   *  by identity instead of array position — an index shifts every time an
   *  earlier note in pendingFormPayload.notes is removed, which would
   *  silently desync which note actually gets removed (the same failure
   *  mode removeSdrLineOffline's skuId-based removal was designed to avoid
   *  for product lines). Optional so existing callers that never set it are
   *  unaffected. */
  clientId?: string;
  /** The real Dynamics annotation id for an already-persisted note being
   *  resumed for editing — undefined for a genuinely new/staged-this-session
   *  note. Lets the notes-section display use the real id (e.g. for
   *  downloading an attachment) instead of a synthetic staged-<index> id. */
  existingAnnotationId?: string;
}

export interface SdrLineItemResult {
  product: SdrProductLine;
  status: 'accepted' | 'rejected';
  /**
   * Dynamics' plugin-thrown message when rejected — e.g. "Limit Exceeded.",
   * "Customer is not eligible for Sample Request for this SKU.", "No
   * Allocation Config found." (Exeevo.AllocationSR.PreOperationSRLineItem).
   * Undefined if the rejection reason couldn't be parsed from the error.
   */
  errorMessage?: string;
  /**
   * Why the line was rejected, for the offline-replay retry decision:
   *   - 'deterministic': a business-rule rejection (HTTP 400 + plugin message).
   *     Retrying can't change the verdict — terminal, surface to the rep.
   *   - 'transient': timeout / 5xx / throttle / network. The line may well
   *     succeed on a later attempt — retry it next sync cycle.
   * Only set when status === 'rejected'.
   */
  kind?: 'deterministic' | 'transient';
}

/**
 * Single-line LIVE autosave counterpart to `SdrLineItemResult` — returned by
 * `addSdrLine` the moment the rep adds one product in the picker, against an
 * already-existing Draft SDR header. `SdrLineItemResult` is the batched
 * result shape used by the initial submit / offline-replay path
 * (`_createSdrLines`, `Promise.allSettled` over many lines at once); this one
 * is for a single independent per-add request instead.
 */
export interface SdrLineAutosaveResult {
  status: 'accepted' | 'rejected';
  /** Set only when status === 'accepted' — the new line's own id, needed for a later removeSdrLine call. */
  lineId?: string;
  /** Dynamics' plugin-thrown rejection message, when parseable. Only set when status === 'rejected'. */
  errorMessage?: string;
  /**
   * Same retry classification as `SdrLineItemResult.kind` — 'deterministic'
   * (business-rule rejection, don't retry) vs 'transient' (timeout/5xx/
   * network, safe to retry). Only set when status === 'rejected'.
   */
  kind?: 'deterministic' | 'transient';
}

/**
 * Classify a Dynamics create rejection for the offline-replay retry decision.
 * Any 4xx means Dynamics rejected the request on its merits — replaying the
 * identical payload can't change the verdict, so it's terminal: a
 * PreOperationSRLineItem business rejection (400 + InvalidPluginExecution-
 * Exception), a create-privilege failure (403), a stale/missing bound
 * reference (404), a duplicate (409), an oversized attachment (413), etc.
 * The two retryable 4xx exceptions are 408 (request timeout) and 429
 * (throttled), which are transient by nature. Everything else — our
 * withTimeout firing (no status), 5xx, or a network/0/unparseable status —
 * is transient, so the line/header is retried rather than dropped; a
 * genuinely permanent error there simply keeps deferring, which stays
 * visible via the offline data count.
 */
function classifySdrFailure(reason: any): 'deterministic' | 'transient' {
  const status: number | undefined = reason?.status ?? reason?.error?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return status === 408 || status === 429 ? 'transient' : 'deterministic';
  }
  return 'transient';
}

export interface SdrCreateResult {
  sdr: SampleDeliveryRequest;
  notes: IONote[];
  /**
   * Per-line outcome from Dynamics' PreOperationSRLineItem plugin — confirmed
   * contract (OMNI-59706): the parent Sample Request is ALWAYS created; each
   * line item is validated and created independently, so an invalid line is
   * simply never created at all (not created-then-flagged), while valid
   * lines land normally and unaffected by any sibling's rejection.
   * `sdr.products` already reflects only the accepted lines — this array
   * additionally carries WHY any line was rejected, for the caller to notify
   * the rep.
   */
  lineResults: SdrLineItemResult[];
  /**
   * Count of staged notes that failed to create (e.g. a timeout on a large
   * attachment). The SDR and its accepted lines already committed by the
   * time notes are attempted, so a note failure never fails the whole
   * submit — but it's surfaced here (confirmed: should notify the rep,
   * not fail silently) rather than only being logged.
   */
  failedNoteCount: number;
}

/**
 * Races `promise` against a timeout so a hung/dropped connection rejects
 * instead of leaving the caller waiting indefinitely. See SDR_SUBMIT_TIMEOUT_MS.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Request timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * Per-SDR cumulative offline attachment ceiling (OMNI-61002). Offline SDRs stash
 * every note attachment's base64 body in the pendingSync PouchDB doc; on a
 * low/mid-range target device (the field-rep fleet) a handful of large images
 * can bloat that single doc enough to risk a PouchDB write failure. The UI
 * already caps each single file (MAXIMUM_NOTE_ATTACHMENT_SIZE); this bounds the
 * SUM across a draft's notes so an offline submit fails fast with a clear toast
 * instead of a mid-write crash. 20MB matches the Samsung A6-class budget.
 */
export const MAX_CUMULATIVE_OFFLINE_SDR_ATTACHMENT_BYTES = 20 * 1024 * 1024;

export function exceedsOfflineSdrAttachmentBudget(notes: StagedSdrNote[]): boolean {
  const total = (notes ?? []).reduce((sum, note) => sum + (note.attachment?.fileSize ?? 0), 0);
  return total > MAX_CUMULATIVE_OFFLINE_SDR_ATTACHMENT_BYTES;
}

/**
 * Offline SDR payload — a superset of createSdr()'s argument (adds the required
 * `sdrId` and optional `appointmentId`) so `uploadPendingSdrs` can replay it by
 * passing it straight back into `createSdr` with no reshaping.
 */
export interface PendingSdrPayload {
  sdrId: string;
  contactId: string;
  deliveryAddress: string;
  email: string;
  products: SdrProductLine[];
  notes: StagedSdrNote[];
  alternateAddress?: string;
  alternateEmail?: string;
  appointmentId?: string;
  /**
   * Display-name counterparts for contactId/deliveryAddress/email — createSdr()
   * doesn't need these (Dynamics returns the FormattedValue on create), but
   * offline has no server round-trip to resolve a lookup id into a label, so
   * these are the ONLY source for what the read-only row shows immediately
   * after an offline submit. Without them the customer/address/email display
   * text is blank even though the ids themselves are stored correctly.
   */
  contactName?: string;
  deliveryAddressLabel?: string;
  emailLabel?: string;
  /**
   * Real Dynamics ids for lines/notes that were already synced BEFORE the
   * rep removed them while offline — distinct from `products`/`notes` above,
   * which only ever hold things still awaiting their first CREATE.
   * removeSdrLineOffline/removeSdrNoteOffline's skuId/clientId filter is a
   * no-op for an already-synced item (it was never in these arrays to begin
   * with), so without this the removal is silently lost the moment the
   * live online delete call fails while offline — never replayed, never
   * actually deleted from Dynamics. Replayed by uploadPendingSdrs' reconcile
   * branch on reconnect (queueSdrLineDeleteOffline/queueSdrNoteDeleteOffline).
   */
  pendingLineDeleteIds?: string[];
  pendingNoteDeleteIds?: string[];
}

/** Payload for createSdrDraft/createSdrDraftOffline — contactId/deliveryAddress/
 *  email are all optional because a multi-contact meeting opens the draft
 *  before any customer is chosen; updateSdrDraftContact patches it in later. */
export interface DraftSdrPayload {
  sdrId: string;
  contactId?: string;
  /** Display label for contactId — same rationale as deliveryAddressLabel below.
   *  Without it an offline-created Draft's Customer field reads blank on every
   *  re-read from disk, since `contactName` is parsed from the customer lookup's
   *  FormattedValue and offline has no create response to supply one. */
  contactName?: string;
  deliveryAddress?: string;
  /** Display label for deliveryAddress — only consumed by createSdrDraftOffline.
   *  createSdrDraft (online) ignores it: Dynamics' create response resolves and
   *  returns the lookup's formatted display name automatically. Offline has no
   *  such round-trip, so without this the address renders blank in the UI
   *  despite deliveryAddress (the id) being correctly set. */
  deliveryAddressLabel?: string;
  email?: string;
  /** Display label for email — same rationale as deliveryAddressLabel. */
  emailLabel?: string;
  alternateAddress?: string;
  alternateEmail?: string;
  appointmentId?: string;
}

@Injectable({ providedIn: 'root' })
export class SampleDeliveryRequestDataService {
  /**
   * Fired from uploadPendingSdrs whenever a pending offline SDR fully commits
   * (header + lines + notes all reconciled server-side) — carries the
   * now-server-hydrated record (real indskr_name, statuscode, etc.), replacing
   * the placeholder created back in createSdrOffline/cacheCreatedSdr.
   *
   * Exposed as a Subject/Observable rather than a direct call: this data
   * service must not depend on SampleDeliveryRequestService, so it can't just
   * call cacheCreatedSdr() itself. Without this, the business service's
   * in-memory `allSdrs` — which fetchSdrsForMeeting's offline branch reads
   * directly, never disk — only ever gets refreshed by reopening the meeting
   * online or by syncRepSdrs() (a *separate*, not-always-run sync-pipeline
   * task). Go offline again before either of those fires and the meeting
   * keeps showing the pre-sync placeholder (raw GUID, blank name) even though
   * the on-disk doc is already correct.
   */
  private readonly _sdrSynced$ = new Subject<SampleDeliveryRequest>();
  readonly sdrSynced$: Observable<SampleDeliveryRequest> = this._sdrSynced$.asObservable();

  constructor(
    private readonly dynamics: DynamicsClientService,
    private readonly disk: DiskService,
    private readonly authService: AuthenticationService,
    private readonly device: DeviceService,
    private readonly activityService: ActivityService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly translate: TranslateService,
    private readonly myAssistantService: MyAssistantService,
  ) {}

  /**
   * Resolve an offline meeting reference to its real Dynamics GUID once the meeting has synced.
   * Mirrors MedicalInquiryDataService.resolveAppointmentRef: an SDR created inside a meeting that
   * was itself created offline stamps `appointmentId` with the temporary `offline_meeting_<uuid>`
   * id (see AppointmentActivity.offlineMeetingId). Meeting upload runs before uploadPendingSdrs in
   * master.data.service, so by the time this runs the in-memory appointment's `.ID` has already
   * been swapped to the real GUID while `.offlineMeetingId` is preserved as the lookup key.
   * Returns '' if the meeting hasn't synced yet — caller should defer the SDR.
   */
  private resolveAppointmentRef(apptRef: string | undefined): string {
    if (!apptRef) return '';
    if (!apptRef.includes('offline')) return apptRef;
    const matched = this.activityService.activities?.find(
      (a) => a instanceof AppointmentActivity
        && (a as AppointmentActivity).offlineMeetingId === apptRef
        && !!a.ID
        && !a.ID.includes('offline')
    );
    return matched?.ID || '';
  }

  /**
   * Submit an SDR. Generates the SDR id client-side so line items and notes
   * can bind to it via @odata.bind.
   *
   * Confirmed contract (OMNI-59706, Exeevo.AllocationSR.PreOperationSRLineItem):
   * the parent Sample Request is created FIRST, standalone, and ALWAYS exists
   * regardless of how its line items validate. Each line item is THEN created
   * as its own independent request — Dynamics validates it individually
   * (customer + SKU + quantity against the Manual-Distribution Customer
   * Allocation) and simply never creates an invalid line, throwing a
   * human-readable message for that line only. This is deliberately NOT a
   * single atomic batch: one line failing must not roll back the header or
   * any sibling line that qualified. See `SdrLineItemResult`.
   *
   * Returns the freshly-saved SDR (products = accepted lines only) plus its
   * annotations and the full per-line accept/reject outcome, so the caller
   * can both render the detail view and notify the rep about any rejections
   * without a follow-up fetch.
   */
  /**
   * Create-on-New (OMNI-61316 follow-up): header-only create, statuscode Active
   * (Draft) instead of Submitted, no product lines or notes yet. Mirrors
   * createSdr's header-build exactly EXCEPT the customer bind is conditional —
   * a multi-contact meeting opens this draft before any customer is chosen
   * (mirrors Sample Drop's own optional indskr_contactid). Call
   * updateSdrDraftContact once a contact is picked/changed afterward.
   */
  async createSdrDraft(payload: DraftSdrPayload): Promise<SampleDeliveryRequest> {
    const sdrBody: Record<string, any> = {
      indskr_samplerequestid: payload.sdrId,
      indskr_type: SDR_TYPE_DELIVERY_REQUEST,
      statuscode: SdrStatus.Active,
      indskr_externalid: `${SDR_EXTERNAL_ID_PREFIX}${payload.sdrId}`,
    };

    const userId = this.authService.user?.systemUserID;
    if (userId) {
      sdrBody[SDR_USER_NAV] = `/systemusers(${userId})`;
    }
    if (payload.appointmentId) {
      sdrBody[SDR_APPOINTMENT_NAV] = `/appointments(${payload.appointmentId})`;
    }
    if (payload.contactId) {
      sdrBody['indskr_Customer_contact@odata.bind'] = `/contacts(${payload.contactId})`;
    }

    const altAddress = payload.alternateAddress?.trim();
    if (altAddress) {
      sdrBody['indskr_alternativeaddress'] = altAddress;
    } else if (payload.deliveryAddress) {
      sdrBody['indskr_Address@odata.bind'] = `/indskr_addresses(${payload.deliveryAddress})`;
    }

    const altEmail = payload.alternateEmail?.trim();
    if (altEmail) {
      sdrBody['indskr_alternativeemailaddress'] = altEmail;
    } else if (payload.email) {
      sdrBody['indskr_EmailAddress@odata.bind'] = `/indskr_email_addresses(${payload.email})`;
    }

    // Left unwrapped by try/catch, same rationale as createSdr's header create
    // above — the throw propagates to the caller.
    const sdrRaw = await withTimeout(this.dynamics.create(sdrBody, SDR_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
    const sdr = new SampleDeliveryRequest(sdrRaw);
    sdr.notesLoaded = true; // freshly created, nothing to lazily hydrate
    return sdr;
  }

  /** Offline sibling of createSdrDraft — writes the Draft record straight to
   *  PouchDB, statuscode Active, pendingSync true. NOT picked up by
   *  uploadPendingSdrs, whose Submitted-only filter skips a still-Active
   *  Draft until finalizeSdr flips its statuscode to Submitted. */
  createSdrDraftOffline(payload: DraftSdrPayload): SampleDeliveryRequest {
    const createdAtIso = new Date().toISOString();
    // Matches the shape createSdrOffline already sends — products/notes start
    // empty since nothing has been added yet at header-creation time; the new
    // *Offline autosave siblings (addSdrLineOffline etc.) append to this same
    // payload as the rep edits, and finalizeSdr flips statuscode to Submitted
    // once they're done, at which point uploadPendingSdrs replays it verbatim.
    const pendingFormPayload: PendingSdrPayload = {
      sdrId: payload.sdrId,
      contactId: payload.contactId ?? '',
      contactName: payload.contactName ?? '',
      deliveryAddress: payload.deliveryAddress ?? '',
      deliveryAddressLabel: payload.deliveryAddressLabel ?? '',
      email: payload.email ?? '',
      emailLabel: payload.emailLabel ?? '',
      products: [],
      notes: [],
      alternateAddress: payload.alternateAddress ?? '',
      alternateEmail: payload.alternateEmail ?? '',
      appointmentId: payload.appointmentId ?? '',
    };
    const rawData: Record<string, any> = {
      indskr_samplerequestid: payload.sdrId,
      indskr_name: '',
      indskr_type: SDR_TYPE_DELIVERY_REQUEST,
      statuscode: SdrStatus.Active,
      indskr_externalid: `${SDR_EXTERNAL_ID_PREFIX}${payload.sdrId}`,
      _indskr_customer_value: payload.contactId ?? '',
      '_indskr_customer_value@OData.Community.Display.V1.FormattedValue': payload.contactName ?? '',
      _indskr_appointment_value: payload.appointmentId ?? '',
      _indskr_address_value: payload.deliveryAddress ?? '',
      // Display labels — online (createSdrDraft) gets these for free from
      // Dynamics' create response; offline has no server round-trip to
      // resolve the lookups' display names, so without these the address/
      // email fields render blank in the UI despite the ids above being
      // correctly set.
      '_indskr_address_value@OData.Community.Display.V1.FormattedValue': payload.deliveryAddressLabel ?? '',
      _indskr_emailaddress_value: payload.email ?? '',
      '_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue': payload.emailLabel ?? '',
      indskr_alternativeaddress: payload.alternateAddress ?? '',
      indskr_alternativeemailaddress: payload.alternateEmail ?? '',
      createdon: createdAtIso,
      pendingSync: true,
      // 'create' (not a draft-specific op) so this round-trips through the
      // exact same uploadPendingSdrs code path createSdrOffline already
      // uses — no special-casing needed there.
      pendingOperation: 'create',
      pendingFormPayload,
    };

    const sdr = new SampleDeliveryRequest(rawData);
    sdr.notesLoaded = true;
    this.disk.updateOrInsert(`${DB_KEY_PREFIXES.SDR_RECORD}${payload.sdrId}`, () => ({ raw: rawData }));
    this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.SAMPLE_DELIVERY_REQUEST, 1);
    return sdr;
  }

  /**
   * Autosave hook for the contact/address/email section of an in-progress
   * inline SDR Draft — fired whenever applyContact() (or an address/email
   * selection) changes the form after createSdrDraft already created the
   * header (e.g. a multi-contact meeting where no contact was auto-selected,
   * or the rep changes the customer mid-edit). Only ever SETS a field when
   * the corresponding value is truthy; it never clears/unbinds a previously
   * -set one. That's sufficient here: validateForSubmit already requires
   * contactId to be present before Submit is enabled, so a rep can never
   * finalize with a stale/cleared contact left dangling on the Draft — they
   * must pick a new contact before Submit works, and that pick is what calls
   * this method with the new value, correctly overwriting whatever was there
   * before.
   */
  async updateSdrDraftContact(
    sdrId: string,
    fields: {
      contactId?: string;
      deliveryAddress?: string;
      email?: string;
      alternateAddress?: string;
      alternateEmail?: string;
    },
  ): Promise<void> {
    const patch = this.buildContactFieldsPatch(fields);
    // Left unwrapped by try/catch, same rationale as createSdrDraft's create
    // above — the throw propagates to the caller (a later task's autosave
    // wiring), which must catch it and decide whether to retry. Unlike a
    // failed create, the Draft header itself is unaffected here; only this
    // specific field patch didn't land.
    await withTimeout(this.dynamics.update(sdrId, SDR_ENTITY, patch), SDR_SUBMIT_TIMEOUT_MS);
  }

  /**
   * Builds the customer/address/email PATCH body shared by updateSdrDraftContact
   * (single awaited call) and _reconcileSdrBatch (queued into a batch instead).
   * Alternate address/email win over the looked-up address/email lookup when set,
   * matching createSdr/_createSdrBatch's own header-construction precedence.
   */
  private buildContactFieldsPatch(fields: {
    contactId?: string;
    deliveryAddress?: string;
    email?: string;
    alternateAddress?: string;
    alternateEmail?: string;
  }): Record<string, any> {
    const patch: Record<string, any> = {};
    if (fields.contactId) {
      patch['indskr_Customer_contact@odata.bind'] = `/contacts(${fields.contactId})`;
    }
    // Picking a SAVED address must clear the manual free text. _initResumeState
    // derives the mode from `!!sdr.alternateAddress`, so leaving the old value
    // behind made a reopen (which refetches, e.g. from Calendar) read it back,
    // flip the form into alternate mode and resurface an address the rep had
    // already replaced (OMNI-62927).
    //
    // Both directions clear (OMNI-62927): a record holding BOTH a lookup and
    // free text is ambiguous to fulfilment, which could ship to the address the
    // rep replaced. Exactly one of each pair stays populated.
    const altAddress = fields.alternateAddress?.trim();
    if (altAddress) {
      patch['indskr_alternativeaddress'] = altAddress;
      patch['indskr_Address@odata.bind'] = null;
    } else if (fields.deliveryAddress) {
      patch['indskr_Address@odata.bind'] = `/indskr_addresses(${fields.deliveryAddress})`;
      patch['indskr_alternativeaddress'] = null;
    }
    const altEmail = fields.alternateEmail?.trim();
    if (altEmail) {
      patch['indskr_alternativeemailaddress'] = altEmail;
      patch['indskr_EmailAddress@odata.bind'] = null;
    } else if (fields.email) {
      patch['indskr_EmailAddress@odata.bind'] = `/indskr_email_addresses(${fields.email})`;
      patch['indskr_alternativeemailaddress'] = null;
    }
    return patch;
  }

  /** Offline sibling of updateSdrDraftContact — an offline-created Draft has
   *  no server-side record to PATCH, so this writes straight to the local
   *  PouchDB doc instead, updating BOTH `pendingFormPayload` (what
   *  uploadPendingSdrs reads to build the eventual create payload) and the
   *  flat `raw._indskr_*_value`/FormattedValue fields (what fromCached/the UI
   *  reads for display) — unlike products/notes, contact/address/email are
   *  not overlaid from pendingFormPayload alone, so both must stay in sync.
   *  Same only-SET-a-truthy-field semantics as the online method. */
  async updateSdrDraftContactOffline(
    sdrId: string,
    fields: {
      contactId?: string;
      contactName?: string;
      deliveryAddress?: string;
      deliveryAddressLabel?: string;
      email?: string;
      emailLabel?: string;
      alternateAddress?: string;
      alternateEmail?: string;
    },
  ): Promise<void> {
    const key = `${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`;
    await this.disk.updateOrInsert(key, (doc: any) => {
      const raw = doc?.raw ?? {
        indskr_samplerequestid: sdrId,
        indskr_type: SDR_TYPE_DELIVERY_REQUEST,
        statuscode: SdrStatus.Active,
        createdon: new Date().toISOString(),
      };
      // A header created ONLINE (via createSdrDraft) and only later synced
      // into local PouchDB by a normal fetch/hydration cycle may have no
      // statuscode on its raw doc at all — uploadPendingSdrs' pending filter
      // requires statuscode to be exactly Active or Submitted, so an
      // undefined value here silently excludes an otherwise-correctly-
      // flagged pendingSync doc from ever being pushed. Backfill it.
      raw.statuscode = raw.statuscode ?? SdrStatus.Active;
      // Display labels are carried over too, not just the lookup ids: they are
      // the only source a disk re-hydration has for the customer/address/email
      // NAMES (fromCached's overlayPendingPayload reads contactName from here),
      // so a doc bootstrapped without them renders a blank customer.
      const payload: PendingSdrPayload = raw.pendingFormPayload ?? {
        sdrId,
        contactId: raw._indskr_customer_value ?? '',
        contactName: raw['_indskr_customer_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        deliveryAddress: raw._indskr_address_value ?? '',
        deliveryAddressLabel: raw['_indskr_address_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        email: raw._indskr_emailaddress_value ?? '',
        emailLabel: raw['_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        products: [],
        notes: [],
        alternateAddress: raw.indskr_alternativeaddress ?? '',
        alternateEmail: raw.indskr_alternativeemailaddress ?? '',
        appointmentId: raw._indskr_appointment_value ?? '',
      };

      if (fields.contactId) {
        payload.contactId = fields.contactId;
        payload.contactName = fields.contactName ?? payload.contactName;
        raw._indskr_customer_value = fields.contactId;
        // Mirrors the address/email FormattedValue writes below — `contactName`
        // is parsed from this key, so without it the Customer field reads blank
        // on every re-read of this doc even though the id is stored correctly.
        raw['_indskr_customer_value@OData.Community.Display.V1.FormattedValue'] =
          fields.contactName ?? raw['_indskr_customer_value@OData.Community.Display.V1.FormattedValue'] ?? '';
      }

      // Same rule as the online _buildContactPatch (OMNI-62927), applied to
      // BOTH the replay payload and the flat raw fields. These must agree, or a
      // draft reads back differently before and after it syncs.
      const altAddress = fields.alternateAddress?.trim();
      if (altAddress) {
        payload.alternateAddress = altAddress;
        raw.indskr_alternativeaddress = altAddress;
        payload.deliveryAddress = '';
        payload.deliveryAddressLabel = '';
        raw._indskr_address_value = '';
        raw['_indskr_address_value@OData.Community.Display.V1.FormattedValue'] = '';
      } else if (fields.deliveryAddress) {
        payload.deliveryAddress = fields.deliveryAddress;
        payload.deliveryAddressLabel = fields.deliveryAddressLabel ?? payload.deliveryAddressLabel;
        raw._indskr_address_value = fields.deliveryAddress;
        raw['_indskr_address_value@OData.Community.Display.V1.FormattedValue'] = fields.deliveryAddressLabel ?? '';
        payload.alternateAddress = '';
        raw.indskr_alternativeaddress = '';
      }

      const altEmail = fields.alternateEmail?.trim();
      if (altEmail) {
        payload.alternateEmail = altEmail;
        raw.indskr_alternativeemailaddress = altEmail;
        payload.email = '';
        payload.emailLabel = '';
        raw._indskr_emailaddress_value = '';
        raw['_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue'] = '';
      } else if (fields.email) {
        payload.email = fields.email;
        payload.emailLabel = fields.emailLabel ?? payload.emailLabel;
        raw._indskr_emailaddress_value = fields.email;
        raw['_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue'] = fields.emailLabel ?? '';
        payload.alternateEmail = '';
        raw.indskr_alternativeemailaddress = '';
      }

      return { raw: { ...raw, pendingSync: true, pendingOperation: 'create', pendingFormPayload: payload } };
    });
  }

  /** Autosave: create one product line against an already-existing Draft SDR,
   *  the moment the rep adds it in the picker. Same independent-request shape
   *  as _createSdrLines, single-line, exposed so the caller learns the new
   *  line's own id (needed for a later removeSdrLine call). */
  async addSdrLine(sdrId: string, product: SdrProductLine): Promise<SdrLineAutosaveResult> {
    const line: Record<string, any> = {
      'indskr_SampleRequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
      'indskr_SKU@odata.bind': `/products(${product.skuId})`,
      indskr_quantity: product.qty,
      // Sample Line Item's own Type option set — same values as the SDR header's
      // indskr_type, but a DIFFERENT logical name (indskr_requesttype). Confirmed
      // against live entity metadata: indskr_type doesn't exist on
      // indskr_samplereconciliation (OMNI-60495 fix was reverted same-day after
      // hitting that OData error), but indskr_requesttype does, with the same
      // OptionSet (268890001 = Delivery Request).
      indskr_requesttype: SDR_TYPE_DELIVERY_REQUEST,
    };
    if (product.productId) {
      line['indskr_Product@odata.bind'] = `/products(${product.productId})`;
    }
    try {
      const raw: any = await withTimeout(this.dynamics.create(line, LINE_ITEM_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
      return { status: 'accepted', lineId: raw?.indskr_samplereconciliationid };
    } catch (reason: any) {
      const errorMessage: string | undefined = reason?.message || reason?.error?.message || undefined;
      return { status: 'rejected', errorMessage, kind: classifySdrFailure(reason) };
    }
  }

  /** Autosave: delete a previously-added line (rep removed it from the picker
   *  before finalizing). Caller only calls this for a line it has an id for —
   *  a rejected addSdrLine has no lineId, so there's nothing to delete.
   *  Throws on failure — unlike addSdrLine, callers must catch/handle this
   *  themselves. */
  async removeSdrLine(lineId: string): Promise<void> {
    console.log(`[SDR-REMOVE] removeSdrLine: deleting ${LINE_ITEM_ENTITY}(${lineId}) from Dynamics`);
    try {
      await withTimeout(this.dynamics.delete(lineId, LINE_ITEM_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
      console.log(`[SDR-REMOVE] removeSdrLine: ${LINE_ITEM_ENTITY}(${lineId}) deleted`);
    } catch (err) {
      console.error(`[SDR-REMOVE] removeSdrLine: delete FAILED for ${LINE_ITEM_ENTITY}(${lineId})`, err);
      throw err;
    }
  }

  /** Best-effort resend of a line's current quantity — used right before
   *  finalize to catch up any qty change the rep made after the line was
   *  first added (there is no live per-keystroke autosave for quantity,
   *  same as contact/address/email — see submit()'s own pre-finalize
   *  re-patch for the analogous reasoning). */
  async updateSdrLineQty(lineId: string, qty: number): Promise<void> {
    await withTimeout(this.dynamics.update(lineId, LINE_ITEM_ENTITY, { indskr_quantity: qty }), SDR_SUBMIT_TIMEOUT_MS);
  }

  /** Autosave: create one staged note/attachment against an already-existing
   *  Draft SDR the moment the rep commits it (SdrNotesSectionComponent's
   *  "Add" button). Returns the new annotation id so the caller can later
   *  call removeSdrNote if the rep deletes it before finalizing. Throws on
   *  failure — unlike addSdrLine, which swallows a rejection into a returned
   *  result object, this propagates straight to the caller. A caller wanting
   *  to tell a retryable failure from a permanent one should run the caught
   *  error through classifySdrFailure(err) itself (the same function
   *  _createSdrLines/addSdrLine already use) — notably, an oversized
   *  attachment (413) is one of the deterministic cases it names, and only a
   *  note create can hit that (line items never carry attachments). */
  async upsertSdrNote(sdrId: string, note: StagedSdrNote): Promise<string> {
    const body: Record<string, any> = {
      notetext: note.noteText ?? '',
      'objectid_indskr_samplerequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
    };
    if (note.attachment) {
      body.documentbody = note.attachment.documentBody;
      body.filename = note.attachment.fileName;
      body.mimetype = note.attachment.mimeType;
      body.filesize = note.attachment.fileSize;
      body.isdocument = true;
    }
    const raw: any = await withTimeout(this.dynamics.create(body, ANNOTATIONS_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
    if (!raw?.annotationid) {
      throw new Error('SDR note create succeeded but returned no annotationid');
    }
    return raw.annotationid;
  }

  /** Autosave: patch an ALREADY-CREATED annotation in place after the rep
   *  edits it via the pencil icon. Deliberately separate from upsertSdrNote —
   *  despite that method's name it only ever calls dynamics.create, so
   *  re-running it for an edit would create a DUPLICATE annotation rather
   *  than update the existing one.
   *  An attachment is only written when the caller passes one. `undefined`
   *  means "untouched, leave whatever is on the record"; explicit removal is
   *  expressed by clearAttachment, which nulls the document fields (Dynamics
   *  keeps isdocument true for a once-document annotation, so the body/name
   *  are what actually have to be cleared). Throws on failure — same
   *  asymmetry as removeSdrNote below. */
  async updateSdrNote(
    annotationId: string,
    note: StagedSdrNote,
    clearAttachment = false,
  ): Promise<void> {
    const body: Record<string, any> = { notetext: note.noteText ?? '' };
    if (note.attachment) {
      body.documentbody = note.attachment.documentBody;
      body.filename = note.attachment.fileName;
      body.mimetype = note.attachment.mimeType;
      body.filesize = note.attachment.fileSize;
      body.isdocument = true;
    } else if (clearAttachment) {
      body.documentbody = null;
      body.filename = null;
      body.mimetype = null;
      body.isdocument = false;
    }
    await withTimeout(
      this.dynamics.update(annotationId, ANNOTATIONS_ENTITY, body),
      SDR_SUBMIT_TIMEOUT_MS,
    );
  }

  /** Offline sibling of updateSdrNote — replaces the matching entry in the
   *  locally-tracked notes array in place, keyed by the note's stable
   *  clientId (same identity convention as removeSdrNoteOffline, and for the
   *  same reason: array position desyncs after any earlier removal). A no-op
   *  if no entry matches, which is correct — nothing was ever staged for it. */
  async updateSdrNoteOffline(sdrId: string, note: StagedSdrNote): Promise<void> {
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      payload.notes = (payload.notes ?? []).map((n) =>
        n.clientId && n.clientId === note.clientId ? note : n,
      );
    });
  }

  /** Autosave: delete a previously-committed staged note (rep deleted it from
   *  the composer before finalizing). Throws on failure — unlike upsertSdrNote's
   *  create, callers must catch/handle this themselves, same asymmetry as
   *  addSdrLine/removeSdrLine above. Delegates to the existing deleteNote
   *  rather than duplicating it — exposed under an SDR-specific name for
   *  call-site clarity in the autosave path. */
  async removeSdrNote(annotationId: string): Promise<void> {
    await this.deleteNote(annotationId);
  }

  /**
   * Finalize an in-progress inline SDR Draft: flips statuscode Active →
   * Submitted on the record createSdrDraft/createSdrDraftOffline already
   * created — no fresh create. Online: a direct patch. Offline: stamps the
   * local doc's statuscode so uploadPendingSdrs' Submitted-only filter (see
   * below) picks it up for replay on reconnect — its existing reconcile
   * branch ("header already exists, append missing lines/notes, AND patch
   * statuscode if the server-side header is still Active") is what runs then,
   * since the header and every line/note already autosaved individually. That
   * reconcile branch now correctly covers both cases: (a) the header was
   * created ONLINE (createSdrDraft) and offline edits only added deltas via
   * the *Offline autosave siblings — uploadPendingSdrs' fetchSdrById finds
   * the still-Active header server-side, patches its statuscode to Submitted
   * (the offline finalize above could only stamp the LOCAL doc, never the
   * server row), then takes the append-missing-lines/notes branch; (b) the
   * header was created OFFLINE too (createSdrDraftOffline) — nothing exists
   * server-side yet, so the lookup comes back empty and the existing atomic
   * createSdr fresh-create branch fires instead (already Submitted from the
   * start, so the reconcile branch's statuscode patch never even runs for
   * this case), exactly as the pre-existing pure-offline-submit flow
   * (createSdrOffline) always did.
   */
  async finalizeSdr(sdrId: string): Promise<void> {
    if (this.device?.isOffline) {
      // Bootstrap first: if the header was created ONLINE (createSdrDraft)
      // and every add/upsert since then also happened ONLINE (addSdrLine/
      // upsertSdrNote — neither touches PouchDB), THIS finalize call can be
      // the very first offline touch, meaning no local doc exists yet at
      // all. A no-op mutate still runs _mutateSdrOfflineDoc's full
      // bootstrap, which seeds a real pendingFormPayload (with sdrId) via
      // the same path the four *Offline autosave siblings rely on —
      // without this, the plain updateOrInsert below would create a doc
      // containing only {statuscode, pendingSync} with no
      // pendingFormPayload.sdrId, which uploadPendingSdrs can never resolve
      // (it skips docs with no payload.sdrId and never clears pendingSync),
      // permanently stranding the SDR.
      await this._mutateSdrOfflineDoc(sdrId, () => {
        // Nothing to mutate — finalize only needs the doc/pendingFormPayload
        // to exist; the statuscode/pendingSync flip is applied separately
        // below, against whatever doc now definitely exists.
      });
      const key = `${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`;
      // Uses updateOrInsert's own callback argument (the CURRENT doc at write
      // time) rather than a separately-retrieved snapshot that could be stale
      // by the time the write lands — same pattern as _mutateSdrOfflineDoc
      // above and the existing saveSdrRecordToDb. Note: this "read the latest
      // doc in the write callback" pattern avoids the lost-update race only
      // under the standard (non-encrypted) DiskService path — encrypted-tenant
      // writes use a different, non-retrying conflict path in DiskService
      // itself (gated by the DATABASE_ENCRYPTION feature action: a single
      // get-then-put keyed on the captured _rev, with a concurrent conflict
      // silently swallowed rather than retried) — a pre-existing
      // characteristic of that service, unrelated to this change.
      await this.disk.updateOrInsert(key, (doc: any) => ({
        raw: {
          ...(doc?.raw ?? {}),
          statuscode: SdrStatus.Submitted,
          // Offline has no server round-trip to resolve the optionset's display
          // label, so without this the Status field renders blank in the UI
          // despite statuscode above being correctly set (OMNI-61543).
          'statuscode@OData.Community.Display.V1.FormattedValue': this.translate.instant('SUBMITTED'),
          pendingSync: true,
        },
      }));
      return;
    }
    // Unwrapped, same as createSdrDraft/updateSdrDraftContact above — but for
    // a different reason: this PATCH is a pure idempotent statuscode flip, no
    // create involved, so there's no risk of a duplicate record on retry (unlike
    // createSdr's header create). A thrown error here is safe to leave
    // unwrapped; the caller can simply retry finalizeSdr again with no ill effect.
    // NOTE: this PATCH is unconditional (no is-it-already-Submitted check) —
    // if any Dynamics-side plugin/workflow fires on this transition regardless
    // of previous state, a retry after a lost-response ack could re-trigger it.
    // Not verified against backend plugin config; flagging for whoever owns that.
    await withTimeout(this.dynamics.update(sdrId, SDR_ENTITY, { statuscode: SdrStatus.Submitted }), SDR_SUBMIT_TIMEOUT_MS);
  }

  /**
   * Scrap (cancel) an in-progress inline SDR Draft — mirrors Sample Drop's
   * per-allocation scrap: the record is kept for audit (statuscode flips
   * Active -> Inactive, never deleted), unlike finalizeSdr this never
   * resends product lines/notes — a scrapped Draft's contents no longer
   * matter to anyone. Only ever valid from Active; callers (the tab's
   * scrap icon) are themselves gated to Active-status items only, same as
   * Sample Drop's own "can't scrap a completed order" rule.
   *
   * Offline: stamps the local doc the same way finalizeSdr's offline branch
   * does, but pendingSync is reconciled by uploadPendingSdrScraps below —
   * a separate, minimal pass from uploadPendingSdrs (which is Submitted-
   * only and resends lines/notes, neither of which apply to a cancel).
   */
  async scrapSdr(sdrId: string): Promise<void> {
    if (this.device?.isOffline) {
      await this._mutateSdrOfflineDoc(sdrId, () => {
        // Nothing to mutate — same bootstrap-only rationale as finalizeSdr's
        // offline branch: ensures a pendingFormPayload.sdrId exists even if
        // this is the very first offline touch on a header created ONLINE.
      });
      const key = `${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`;
      await this.disk.updateOrInsert(key, (doc: any) => ({
        raw: { ...(doc?.raw ?? {}), statecode: SDR_INACTIVE_STATECODE, statuscode: SdrStatus.Inactive, pendingSync: true },
      }));
      return;
    }
    await withTimeout(
      this.dynamics.update(sdrId, SDR_ENTITY, { statecode: SDR_INACTIVE_STATECODE, statuscode: SdrStatus.Inactive }),
      SDR_SUBMIT_TIMEOUT_MS,
    );
  }

  /**
   * Replay pending offline SDR scraps on reconnect. Deliberately separate
   * from uploadPendingSdrs (Submitted-only, resends lines/notes) — a
   * scrapped Draft has no lines/notes worth resending, it's a pure cancel.
   * Reconcile-lookup-first, same caution as uploadPendingSdrs' own Active
   * check (line ~1089): only patches Dynamics if the record is STILL Active
   * there — never blindly overwrites a status some other path (e.g. this
   * same Draft finalized from a different device/session) has already
   * moved past Active. If the header never reached Dynamics at all (a
   * Draft created and scrapped entirely offline, never synced), there's
   * nothing to patch — just clears the local pendingSync flag either way,
   * since this device's own scrap intent is resolved regardless of which
   * branch ran.
   */
  async uploadPendingSdrScraps(): Promise<void> {
    if (this.device?.isOffline) return;
    try {
      const docs = (await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_SDR_RECORDS, true, true)) as Array<{ _id?: string; raw?: Record<string, any> }>;
      const pending = (docs || []).filter(
        (d) => d?.raw && d.raw.pendingSync === true && d.raw.statuscode === SdrStatus.Inactive,
      );
      if (!pending.length) return;
      for (const doc of pending) {
        const sdrId = doc.raw?.pendingFormPayload?.sdrId || doc.raw?.indskr_samplerequestid;
        if (!sdrId) continue;
        const key = `${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`;
        try {
          const existing = await this.fetchSdrById(sdrId);
          if (existing && existing.statusCode === SdrStatus.Active) {
            await withTimeout(
              this.dynamics.update(sdrId, SDR_ENTITY, { statecode: SDR_INACTIVE_STATECODE, statuscode: SdrStatus.Inactive }),
              SDR_SUBMIT_TIMEOUT_MS,
            );
          }
          await this.disk.updateOrInsert(key, (d: any) => ({
            raw: { ...(d?.raw ?? {}), pendingSync: false },
          }));
        } catch (err) {
          console.error(`[SDR-SYNC] uploadPendingSdrScraps: failed for ${sdrId}, will retry next cycle`, err);
        }
      }
    } catch (err) {
      console.error('[SDR-SYNC] uploadPendingSdrScraps failed', err);
    }
  }

  /** Shared read-modify-write for the four offline autosave siblings below
   *  (and finalizeSdr's offline branch, which calls this with a no-op mutate
   *  purely for its bootstrap effect) — uses updateOrInsert's own callback
   *  argument (the CURRENT doc at write time), never a separately-retrieved
   *  stale snapshot, to avoid the lost-update race a naive retrieve()-then-
   *  write would have. Lazily builds a pendingFormPayload if none exists yet
   *  (the header was created ONLINE via createSdrDraft, which never touches
   *  PouchDB, so no local doc exists until the first offline touch forces one
   *  into existence here).
   *
   *  KNOWN LIMITATION: when bootstrapping from scratch (no local doc at all
   *  yet), the fallback pendingFormPayload can only pull contactId/
   *  deliveryAddress/email/appointmentId (and their display labels) off the
   *  just-fabricated stub `raw` above, which has none of those set — they come
   *  out blank ('') rather than the header's real values. Harmless for
   *  uploadPendingSdrs' reconcile branch (which only appends missing
   *  lines/notes here, and never re-reads these blanked fields once the header
   *  already exists server-side), but the locally-cached PouchDB doc itself
   *  still displays a blank customer/address/email if something re-hydrates
   *  from disk before the next successful sync. The detail view now resolves
   *  the customer name from contactId against the local contact store
   *  (OMNI-61603), which covers the display side whenever contactId survived;
   *  a doc bootstrapped with a blank contactId too is still unresolvable and
   *  would need the real header values threaded into this path. */
  private async _mutateSdrOfflineDoc<T = void>(sdrId: string, mutate: (payload: PendingSdrPayload) => T): Promise<T> {
    const key = `${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`;
    let result: T | undefined;
    await this.disk.updateOrInsert(key, (doc: any) => {
      const raw = doc?.raw ?? {
        indskr_samplerequestid: sdrId,
        indskr_type: SDR_TYPE_DELIVERY_REQUEST,
        statuscode: SdrStatus.Active,
        createdon: new Date().toISOString(),
      };
      // A header created ONLINE (via createSdrDraft) and only later synced
      // into local PouchDB by a normal fetch/hydration cycle may have no
      // statuscode on its raw doc at all — uploadPendingSdrs' pending filter
      // requires statuscode to be exactly Active or Submitted, so an
      // undefined value here silently excludes an otherwise-correctly-
      // flagged pendingSync doc from ever being pushed. Backfill it.
      raw.statuscode = raw.statuscode ?? SdrStatus.Active;
      // Display labels are carried over too, not just the lookup ids: they are
      // the only source a disk re-hydration has for the customer/address/email
      // NAMES (fromCached's overlayPendingPayload reads contactName from here),
      // so a doc bootstrapped without them renders a blank customer.
      const payload: PendingSdrPayload = raw.pendingFormPayload ?? {
        sdrId,
        contactId: raw._indskr_customer_value ?? '',
        contactName: raw['_indskr_customer_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        deliveryAddress: raw._indskr_address_value ?? '',
        deliveryAddressLabel: raw['_indskr_address_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        email: raw._indskr_emailaddress_value ?? '',
        emailLabel: raw['_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue'] ?? '',
        products: [],
        notes: [],
        alternateAddress: raw.indskr_alternativeaddress ?? '',
        alternateEmail: raw.indskr_alternativeemailaddress ?? '',
        appointmentId: raw._indskr_appointment_value ?? '',
      };
      result = mutate(payload);
      return { raw: { ...raw, pendingSync: true, pendingOperation: 'create', pendingFormPayload: payload } };
    });
    return result as T;
  }

  /** Offline sibling of addSdrLine — appends to the locally-tracked product
   *  list instead of hitting Dynamics. Reconciled by uploadPendingSdrs once
   *  finalizeSdr flips this doc to Submitted (its existing SKU-dedup against
   *  the fetched header, if one already exists online, means an already-
   *  synced product from a prior online addSdrLine call is never duplicated). */
  async addSdrLineOffline(sdrId: string, product: SdrProductLine): Promise<void> {
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      payload.products = [...(payload.products ?? []), product];
    });
  }

  /** Offline sibling of removeSdrLine — removes by skuId (there is no
   *  server-assigned line id to remove by while offline; SKU is unique per
   *  SDR, matching uploadPendingSdrs' own existing dedup key). */
  async removeSdrLineOffline(sdrId: string, skuId: string): Promise<void> {
    console.log(`[SDR-REMOVE] removeSdrLineOffline: sdrId=${sdrId} skuId=${skuId} — local pendingFormPayload removal only, NO Dynamics call`);
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      const before = payload.products?.length ?? 0;
      payload.products = (payload.products ?? []).filter((p) => p.skuId !== skuId);
      const after = payload.products.length;
      if (before === after) {
        console.warn(`[SDR-REMOVE] removeSdrLineOffline: sdrId=${sdrId} skuId=${skuId} matched NOTHING in pendingFormPayload.products — if this line was already synced to Dynamics, it was never actually deleted there`);
      }
    });
  }

  /** Sibling of removeSdrLineOffline for a line that WAS already synced
   *  before the rep removed it while offline (a real Dynamics id exists) —
   *  removeSdrLineOffline's skuId-filter would silently no-op for it (never
   *  in pendingFormPayload.products to begin with), so this queues the id
   *  for uploadPendingSdrs' reconcile branch to actually delete on reconnect
   *  instead. See PendingSdrPayload.pendingLineDeleteIds' own doc comment. */
  async queueSdrLineDeleteOffline(sdrId: string, lineId: string): Promise<void> {
    console.log(`[SDR-REMOVE] queueSdrLineDeleteOffline: sdrId=${sdrId} lineId=${lineId} — queued for delete on reconnect, NOT yet removed from Dynamics`);
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      if (!(payload.pendingLineDeleteIds ?? []).includes(lineId)) {
        payload.pendingLineDeleteIds = [...(payload.pendingLineDeleteIds ?? []), lineId];
      }
    });
    // Read back what actually persisted — don't just trust the write above
    // didn't throw. Confirms both that pendingLineDeleteIds landed on disk
    // AND that pendingSync is true (uploadPendingSdrs' own pending filter),
    // since a doc that fails that second check would never be picked up at
    // all regardless of what's in pendingLineDeleteIds.
    try {
      const verifyDoc: any = await this.disk.retrieve(`${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`, true);
      console.log(`[SDR-REMOVE] queueSdrLineDeleteOffline: verify sdrId=${sdrId} pendingSync=${verifyDoc?.raw?.pendingSync} statuscode=${verifyDoc?.raw?.statuscode} pendingLineDeleteIds=${JSON.stringify(verifyDoc?.raw?.pendingFormPayload?.pendingLineDeleteIds)}`);
    } catch (err) {
      console.error(`[SDR-REMOVE] queueSdrLineDeleteOffline: verify read-back FAILED for sdrId=${sdrId}`, err);
    }
  }

  /** Offline sibling — patches the quantity of the matching line (by skuId,
   *  same identity convention as removeSdrLineOffline) inside the locally
   *  tracked pendingFormPayload.products. */
  async updateSdrLineQtyOffline(sdrId: string, skuId: string, qty: number): Promise<void> {
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      const product = (payload.products ?? []).find((p) => p.skuId === skuId);
      if (product) product.qty = qty;
    });
  }

  /** Offline sibling of upsertSdrNote — appends to the locally-tracked note list. */
  async upsertSdrNoteOffline(sdrId: string, note: StagedSdrNote): Promise<void> {
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      payload.notes = [...(payload.notes ?? []), note];
    });
  }

  /** Offline sibling of removeSdrNote — removes by the note's stable
   *  clientId (stamped on the StagedSdrNote when it's first added), NOT by
   *  array position. A raw index would desync after any earlier note in
   *  pendingFormPayload.notes is removed (every later note shifts down),
   *  silently causing the wrong note to be removed or survive — the same
   *  failure mode removeSdrLineOffline's skuId-based removal was designed
   *  to avoid for product lines. */
  async removeSdrNoteOffline(sdrId: string, clientId: string): Promise<void> {
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      payload.notes = (payload.notes ?? []).filter((n) => n.clientId !== clientId);
    });
  }

  /** Sibling of removeSdrNoteOffline for a note that WAS already synced
   *  before the rep removed it while offline (a real annotation id exists) —
   *  same rationale as queueSdrLineDeleteOffline above. */
  async queueSdrNoteDeleteOffline(sdrId: string, annotationId: string): Promise<void> {
    console.log(`[SDR-REMOVE] queueSdrNoteDeleteOffline: sdrId=${sdrId} annotationId=${annotationId} — queued for delete on reconnect, NOT yet removed from Dynamics`);
    await this._mutateSdrOfflineDoc(sdrId, (payload) => {
      if (!(payload.pendingNoteDeleteIds ?? []).includes(annotationId)) {
        payload.pendingNoteDeleteIds = [...(payload.pendingNoteDeleteIds ?? []), annotationId];
      }
    });
    // Read back what actually persisted — see queueSdrLineDeleteOffline's
    // own verify log for why this doesn't just trust the write.
    try {
      const verifyDoc: any = await this.disk.retrieve(`${DB_KEY_PREFIXES.SDR_RECORD}${sdrId}`, true);
      console.log(`[SDR-REMOVE] queueSdrNoteDeleteOffline: verify sdrId=${sdrId} pendingSync=${verifyDoc?.raw?.pendingSync} statuscode=${verifyDoc?.raw?.statuscode} pendingNoteDeleteIds=${JSON.stringify(verifyDoc?.raw?.pendingFormPayload?.pendingNoteDeleteIds)}`);
    } catch (err) {
      console.error(`[SDR-REMOVE] queueSdrNoteDeleteOffline: verify read-back FAILED for sdrId=${sdrId}`, err);
    }
  }

  async createSdr(payload: {
    contactId: string;
    deliveryAddress: string;
    email: string;
    products: SdrProductLine[];
    notes: StagedSdrNote[];
    alternateAddress?: string;
    alternateEmail?: string;
    /**
     * Caller-supplied client id for the SDR. The business service generates it
     * BEFORE the call and remembers it, so that a submit which fails on the
     * response leg (network drop after the server committed) can be reconciled
     * by this exact id instead of being blindly re-posted as a duplicate. Falls
     * back to a fresh GUID for any caller that doesn't need reconcile.
     */
    sdrId?: string;
    /** OMNI-61002: links the SDR to the meeting it was raised from. */
    appointmentId?: string;
    /**
     * Target statuscode for the header create — defaults to Submitted (every
     * pre-existing caller, submit()/submitOffline()'s reconcile, always meant
     * a final create). uploadPendingSdrs' fresh-create branch overrides this
     * with the LOCAL doc's own statuscode, so a still-unfinalized Draft whose
     * header is only now reaching Dynamics (rep reconnected, hasn't tapped
     * Submit yet) is created Active, not silently auto-submitted.
     */
    statuscode?: SdrStatus;
    /**
     * OMNI-60544 branch follow-up: when true and the target statuscode
     * resolves to Submitted, the header is created Active and only PATCHed
     * to Submitted AFTER line items + notes are created, instead of being
     * created Submitted up front. Dynamics' "Exeevo - AutoPopulate - Sample
     * Line Item" sync workflow rejects every line create against a header
     * that is already Submitted ("Sample line items cannot be added or
     * changed because the Sample Request is no longer active.") — confirmed
     * live from the standalone create form, where the header (created
     * Submitted-first, per the old default above) always came back with zero
     * accepted lines. The inline flow never hit this: it creates its header
     * Active via createSdrDraft/createSdrDraftOffline and only flips it to
     * Submitted via finalizeSdr AFTER every line/note already exists.
     *
     * Defaults to false so every OTHER caller — specifically uploadPendingSdrs'
     * fresh-create branch above, which this same createSdr also serves for
     * the inline flow's own fully-offline-then-sync corner case (see
     * finalizeSdr's doc comment, case (b)) — keeps its exact prior behavior.
     * Only SampleDeliveryRequestService.submit() (the standalone form's only
     * online-submit path) opts in.
     */
    createDraftFirst?: boolean;
  }): Promise<SdrCreateResult> {
    const sdrId = payload.sdrId ?? Guid.create().toString();
    const targetStatus = payload.statuscode ?? SdrStatus.Submitted;
    const finalizeAfterLines = !!payload.createDraftFirst && targetStatus === SdrStatus.Submitted;

    const sdrBody: Record<string, any> = {
      indskr_samplerequestid: sdrId,
      indskr_type: SDR_TYPE_DELIVERY_REQUEST,
      statuscode: finalizeAfterLines ? SdrStatus.Active : targetStatus,
      indskr_externalid: `${SDR_EXTERNAL_ID_PREFIX}${sdrId}`,
    };
    if (payload.contactId) {
      sdrBody['indskr_Customer_contact@odata.bind'] = `/contacts(${payload.contactId})`;
    }

    const userId = this.authService.user?.systemUserID;
    if (userId) {
      sdrBody[SDR_USER_NAV] = `/systemusers(${userId})`;
    }

    // OMNI-61002: link the SDR to the meeting it was raised from. See
    // SDR_APPOINTMENT_NAV above — the capitalized guess failed live; trying the
    // lowercase form now.
    if (payload.appointmentId) {
      sdrBody[SDR_APPOINTMENT_NAV] = `/appointments(${payload.appointmentId})`;
    }

    // Address: a free-text alternate (no GUID) is stored as text on
    // indskr_AlternativeAddress; otherwise bind the saved address lookup. The
    // two are mutually exclusive per field (confirmed schema O3).
    const altAddress = payload.alternateAddress?.trim();
    if (altAddress) {
      sdrBody['indskr_alternativeaddress'] = altAddress;
    } else if (payload.deliveryAddress) {
      sdrBody['indskr_Address@odata.bind'] = `/indskr_addresses(${payload.deliveryAddress})`;
    }

    const altEmail = payload.alternateEmail?.trim();
    if (altEmail) {
      sdrBody['indskr_alternativeemailaddress'] = altEmail;
    } else if (payload.email) {
      sdrBody['indskr_EmailAddress@odata.bind'] = `/indskr_email_addresses(${payload.email})`;
    }

    // Header first, standalone, awaited — the line items below bind to it by
    // id, so it must actually exist server-side before any of them are sent.
    // `DynamicsClientService.create()` defaults to return=representation +
    // includeAnnotations='*' (its lazily-built DWA instance), so the raw
    // result already carries the FormattedValue display names the detail
    // view needs — no follow-up GET. Left unwrapped by try/catch: a throw
    // here propagates straight to submit(), which keeps pendingReconcileId
    // set for the reconcile retry, same as before.
    const sdrRaw = await withTimeout(this.dynamics.create(sdrBody, SDR_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
    const sdr = new SampleDeliveryRequest(sdrRaw);
    // Belt-and-suspenders: force appointmentId to the caller's own known value rather
    // than trusting the create response to echo back the just-bound @odata.bind lookup
    // (the SDR_APPOINTMENT_NAV field name above was already a live guess-and-fix once —
    // see the comment on it — and a freshly-bound single-valued navigation property is
    // not always present on a POST's return=representation body). A missing/empty
    // appointmentId here silently breaks the meeting's mandatory-inline-activity check
    // (MeetingMandatoryActivitiesValidationService), which links by this exact field.
    if (payload.appointmentId) {
      sdr.appointmentId = payload.appointmentId;
    }

    const lineResults = await this._createSdrLines(sdrId, payload.products);

    // A rejected line was never created — the SDR must reflect Dynamics'
    // actual state, not the form's optimistic product list.
    sdr.products = lineResults
      .filter((r) => r.status === 'accepted')
      .map((r) => ({ ...r.product }));

    const { notes, failedNoteCount } = await this._createSdrNotes(sdrId, payload.notes ?? []);

    sdr.notes = notes;
    // The created record carries its notes and accepted lines in hand, so it
    // is fully hydrated — the detail view must not re-fetch/read-cache it.
    sdr.notesLoaded = true;

    if (finalizeAfterLines) {
      // Lines/notes now exist against the still-Active header created above —
      // safe to flip to Submitted. Unwrapped like the header create above: a
      // throw here propagates to submit(), which keeps pendingReconcileId set
      // (reconcileSubmit's fetchSdrById will find the header + lines/notes
      // already committed, just not yet flipped, on a retry).
      //
      // `this.dynamics`'s shared dwa instance defaults to returnRepresentation
      // + includeAnnotations: '*' (see DynamicsClientService.dwa), so this
      // PATCH's response already carries the updated
      // statuscode@OData.Community.Display.V1.FormattedValue — capture it
      // instead of only patching the numeric statusCode. sdr.statusLabel
      // (the raw Dynamics-configured display text, e.g. "Submitted") is what
      // the detail view's status pill actually renders
      // (SampleDeliveryRequestDetailComponent.detailsHeader reads
      // sdr.statusLabel, not sdr.statusCode) — without this, the pill stayed
      // stuck on "Active" (the label from the initial create response) even
      // though the record's numeric statuscode, and Dynamics itself, had
      // already moved to Submitted.
      const patched = await withTimeout(
        this.dynamics.update(sdrId, SDR_ENTITY, { statuscode: SdrStatus.Submitted }),
        SDR_SUBMIT_TIMEOUT_MS,
      );
      sdr.statusCode = SdrStatus.Submitted;
      sdr.statusLabel = patched?.['statuscode@OData.Community.Display.V1.FormattedValue'] ?? sdr.statusLabel;
    }
    return { sdr, notes, lineResults, failedNoteCount };
  }

  /**
   * Create the given staged notes/attachments against an already-existing SDR
   * header, each as an independent Promise.allSettled create so one rejection
   * never fails the others (or the whole submit — the header + lines already
   * committed). Shared by createSdr (initial submit) and uploadPendingSdrs'
   * append-lines branch (a header that committed but whose response leg dropped
   * before notes were ever attempted). Returns the hydrated IONote[] plus the
   * count that failed, so the caller can surface a note loss rather than drop
   * it silently. The caller must only pass notes not yet created server-side —
   * uploadPendingSdrs strips notes from the pending payload once createSdr has
   * created them, so a retry never double-creates.
   */
  private async _createSdrNotes(sdrId: string, stagedNotes: StagedSdrNote[]): Promise<{ notes: IONote[]; failedNoteCount: number }> {
    const staged = stagedNotes ?? [];
    const noteOutcomes = await Promise.allSettled(
      staged.map((note) => {
        const body: Record<string, any> = {
          notetext: note.noteText ?? '',
          'objectid_indskr_samplerequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
        };
        if (note.attachment) {
          body.documentbody = note.attachment.documentBody;
          body.filename = note.attachment.fileName;
          body.mimetype = note.attachment.mimeType;
          body.filesize = note.attachment.fileSize;
          body.isdocument = true;
        }
        return withTimeout(this.dynamics.create(body, ANNOTATIONS_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
      }),
    );

    let failedNoteCount = 0;
    const notes: IONote[] = noteOutcomes
      .map((outcome, idx) => {
        if (outcome.status === 'rejected') {
          // A note failing (e.g. a timeout on a large attachment) must not
          // fail the whole submit — the SDR and its line items already
          // committed. Logged AND counted (confirmed: surface this to the
          // rep, don't drop it silently) — see SdrCreateResult.failedNoteCount.
          console.error('SDR note create failed', outcome.reason);
          failedNoteCount++;
          return null;
        }
        const anno: any = outcome.value;
        const stagedNote = staged[idx];
        // documentbody is intentionally NOT stored on the IONote — io-note's
        // download path falls back to IoFileService.getDocumentBody(annotationId)
        // when documentDataURL is empty.
        return new IONote({
          annotationid: anno?.annotationid,
          notetext: anno?.notetext ?? stagedNote?.noteText ?? '',
          filename: anno?.filename ?? stagedNote?.attachment?.fileName ?? '',
          mimetype: anno?.mimetype ?? stagedNote?.attachment?.mimeType ?? '',
          filesize: anno?.filesize ?? stagedNote?.attachment?.fileSize ?? 0,
          isdocument: anno?.isdocument ?? !!stagedNote?.attachment,
          createdon: anno?.createdon,
          // The rep just created these notes — stamp the creator so the detail
          // view shows the owner immediately (the reopen path reads it from the
          // annotation owner lookup instead). Mirrors the staged-note view shape.
          ownerName: anno?.['_ownerid_value@OData.Community.Display.V1.FormattedValue'] ?? this.authService.user?.displayName ?? '',
          ownerid: anno?._ownerid_value ?? this.authService.user?.systemUserID ?? '',
        });
      })
      .filter((n): n is IONote => n !== null);

    return { notes, failedNoteCount };
  }

  /**
   * Create the given product lines against an already-existing SDR header, each
   * as an independent Promise.allSettled create so one rejection never affects
   * the others. Shared by createSdr (initial submit) and uploadPendingSdrs
   * (retrying only the not-yet-committed lines against a header that already
   * synced). The caller is responsible for passing only lines that don't yet
   * exist server-side — uploadPendingSdrs dedups by SKU against the fetched
   * existing SDR before calling this on a retry, so re-creates never duplicate.
   */
  private async _createSdrLines(sdrId: string, products: SdrProductLine[]): Promise<SdrLineItemResult[]> {
    const outcomes = await Promise.allSettled(
      (products ?? []).map((p) => {
        const line: Record<string, any> = {
          'indskr_SampleRequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
          'indskr_SKU@odata.bind': `/products(${p.skuId})`,
          indskr_quantity: p.qty,
          // See addSdrLine's comment — indskr_requesttype, not indskr_type.
          indskr_requesttype: SDR_TYPE_DELIVERY_REQUEST,
        };
        if (p.productId) {
          line['indskr_Product@odata.bind'] = `/products(${p.productId})`;
        }
        return withTimeout(this.dynamics.create(line, LINE_ITEM_ENTITY), SDR_SUBMIT_TIMEOUT_MS);
      }),
    );

    return outcomes.map((outcome, idx) => {
      const product = products[idx];
      if (outcome.status === 'fulfilled') {
        return { product, status: 'accepted' as const };
      }
      // dynamics-web-api's ErrorHelper.handleHttpError copies the OData error
      // envelope's `message` straight onto the rejected Error — this IS the
      // plugin's InvalidPluginExecutionException text (e.g. "Limit Exceeded.").
      const reason: any = outcome.reason;
      const errorMessage: string | undefined = reason?.message || reason?.error?.message || undefined;
      return { product, status: 'rejected' as const, errorMessage, kind: classifySdrFailure(reason) };
    });
  }

  /**
   * Offline-sync counterpart to the reconcile branch's old per-call sequence
   * (contact/address/email patch, missing-line creates, note creates, and a
   * conditional status-completion update) — now ONE Dynamics $batch with
   * `continueOnError` instead of up to four independent round trips.
   *
   * Unlike _createSdrBatch, this is deliberately NOT atomic: continueOnError
   * (see DynamicsClientService.executeBatch's own doc comment) keeps every
   * request's outcome independent — a rejected line doesn't roll back its
   * siblings or the header patch — matching the per-line independence
   * _createSdrLines/_createSdrNotes already gave this branch via
   * Promise.allSettled, just over one HTTP round trip instead of N.
   *
   * Requests within a changeset still execute strictly in the order queued
   * even with continueOnError, so queuing the status-completion update LAST
   * is what fixes OMNI-61561: this branch used to PATCH statuscode to
   * Submitted BEFORE creating the still-missing lines, and Dynamics'
   * "Exeevo - AutoPopulate - Sample Line Item" plugin rejects any line item
   * create/change once the header is no longer Active — silently dropping
   * every line added after an earlier partial (still-Active) sync of the
   * same SDR (e.g. a brief connectivity blip synced the header + whichever
   * products existed at that moment, before the rep finished adding more
   * and tapped Submit).
   */
  private async _reconcileSdrBatch(params: {
    sdrId: string;
    existingStatusCode: SdrStatus;
    localStatuscode: SdrStatus;
    contactPatch?: Record<string, any>;
    missingProducts: SdrProductLine[];
    notes: StagedSdrNote[];
  }): Promise<{ lineResults: SdrLineItemResult[]; notes: IONote[]; failedNoteCount: number; statusPatched: boolean }> {
    const { sdrId, existingStatusCode, localStatuscode, contactPatch, missingProducts, notes } = params;
    // MUST be an equality check against Active specifically, not an inequality
    // against Submitted — see the reconcile branch's own comment in
    // uploadPendingSdrs for why (SdrStatus has post-Submitted states driven by
    // an external 3PL workflow that must never be patched backward).
    const shouldPatchStatus = existingStatusCode === SdrStatus.Active && localStatuscode === SdrStatus.Submitted;

    if (!contactPatch && !missingProducts.length && !notes.length && !shouldPatchStatus) {
      return { lineResults: [], notes: [], failedNoteCount: 0, statusPatched: false };
    }

    type BatchOp =
      | { kind: 'contact' }
      | { kind: 'line'; product: SdrProductLine }
      | { kind: 'note'; note: StagedSdrNote }
      | { kind: 'status' };
    const ops: BatchOp[] = [];

    this.dynamics.startBatch({ returnRepresentation: true, includeAnnotations: '*' });
    try {
      if (contactPatch) {
        this.dynamics.update(sdrId, SDR_ENTITY, contactPatch, 'return=representation');
        ops.push({ kind: 'contact' });
      }

      for (const p of missingProducts) {
        const line: Record<string, any> = {
          'indskr_SampleRequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
          'indskr_SKU@odata.bind': `/products(${p.skuId})`,
          indskr_quantity: p.qty,
          // See addSdrLine's comment — indskr_requesttype, not indskr_type.
          indskr_requesttype: SDR_TYPE_DELIVERY_REQUEST,
        };
        if (p.productId) {
          line['indskr_Product@odata.bind'] = `/products(${p.productId})`;
        }
        this.dynamics.createRequest({ collection: LINE_ITEM_ENTITY, data: line, returnRepresentation: true });
        ops.push({ kind: 'line', product: p });
      }

      for (const note of notes) {
        const body: Record<string, any> = {
          notetext: note.noteText ?? '',
          'objectid_indskr_samplerequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
        };
        if (note.attachment) {
          body.documentbody = note.attachment.documentBody;
          body.filename = note.attachment.fileName;
          body.mimetype = note.attachment.mimeType;
          body.filesize = note.attachment.fileSize;
          body.isdocument = true;
        }
        this.dynamics.createRequest({ collection: ANNOTATIONS_ENTITY, data: body, returnRepresentation: true });
        ops.push({ kind: 'note', note });
      }

      // Status completion — its own distinct batch operation, ALWAYS LAST.
      // See this method's own doc comment for why the ordering here is
      // load-bearing, not cosmetic.
      if (shouldPatchStatus) {
        this.dynamics.update(sdrId, SDR_ENTITY, { statuscode: SdrStatus.Submitted }, 'return=representation');
        ops.push({ kind: 'status' });
      }
    } catch (err) {
      this.dynamics.abortBatch();
      throw err;
    }

    const batchResults: any[] = await withTimeout(this.dynamics.executeBatch({ continueOnError: true }), SDR_SUBMIT_TIMEOUT_MS);

    const lineResults: SdrLineItemResult[] = [];
    const noteResults: IONote[] = [];
    let failedNoteCount = 0;
    let statusPatched = false;

    ops.forEach((op, i) => {
      const result = batchResults[i];
      if (op.kind === 'contact') {
        if (result instanceof Error) {
          console.error(`[SDR-SYNC] _reconcileSdrBatch: ${sdrId} contact/address/email patch rejected`, result);
        }
        return;
      }
      if (op.kind === 'line') {
        if (result instanceof Error) {
          // Same extraction _createSdrLines uses — dynamics-web-api's
          // ErrorHelper.handleHttpError copies the OData error envelope's
          // `message` straight onto the rejected Error.
          const errorMessage: string | undefined = (result as any)?.message || (result as any)?.error?.message || undefined;
          lineResults.push({ product: op.product, status: 'rejected', errorMessage, kind: classifySdrFailure(result) });
        } else {
          lineResults.push({ product: op.product, status: 'accepted' });
        }
        return;
      }
      if (op.kind === 'note') {
        if (result instanceof Error) {
          console.error(`[SDR-SYNC] _reconcileSdrBatch: ${sdrId} note create failed`, result);
          failedNoteCount++;
        } else {
          const anno: any = result;
          noteResults.push(
            new IONote({
              annotationid: anno?.annotationid,
              notetext: anno?.notetext ?? op.note.noteText ?? '',
              filename: anno?.filename ?? op.note.attachment?.fileName ?? '',
              mimetype: anno?.mimetype ?? op.note.attachment?.mimeType ?? '',
              filesize: anno?.filesize ?? op.note.attachment?.fileSize ?? 0,
              isdocument: anno?.isdocument ?? !!op.note.attachment,
              createdon: anno?.createdon,
              ownerName: anno?.['_ownerid_value@OData.Community.Display.V1.FormattedValue'] ?? this.authService.user?.displayName ?? '',
              ownerid: anno?._ownerid_value ?? this.authService.user?.systemUserID ?? '',
            }),
          );
        }
        return;
      }
      if (op.kind === 'status') {
        if (result instanceof Error) {
          console.error(`[SDR-SYNC] _reconcileSdrBatch: ${sdrId} status-completion patch rejected`, result);
        } else {
          statusPatched = true;
        }
      }
    });

    return { lineResults, notes: noteResults, failedNoteCount, statusPatched };
  }

  /**
   * Offline-sync counterpart to createSdr(), used ONLY by uploadPendingSdrs'
   * fresh-create branch (an SDR whose header never reached Dynamics at all).
   * Sends the header create, every product line create, every note create,
   * and — only when the local doc is actually finalized (Submitted) — the
   * status-completion update, as ONE Dynamics $batch (one HTTP round trip)
   * instead of createSdr's N independent calls. The status-completion PATCH
   * is always its own distinct operation, never folded into the header
   * create body — the header is always created Active, matching
   * createSdrDraft's own Active-on-create convention.
   *
   * Deliberately atomic, unlike createSdr (see createSdr's own doc comment:
   * a line rejection there must NOT roll back the header or its siblings).
   * A batch changeset is all-or-nothing by construction — dynamics-web-api
   * groups every non-GET request into a single changeset with no public API
   * to split it, so there is no way to keep per-line independence while
   * still sending everything as one physical request. Confirmed acceptable
   * for this path (product decision): an offline SDR's contents were
   * already fully composed before ever touching the network, so "the whole
   * thing lands together or the whole thing stays pending for retry" is the
   * intended behavior — a rejected line here simply classifies the ENTIRE
   * SDR deterministic/transient via the catch in uploadPendingSdrs, the same
   * as a plain header rejection always was, rather than createSdr's
   * per-line accept/reject split.
   */
  private async _createSdrBatch(payload: {
    contactId: string;
    deliveryAddress: string;
    email: string;
    products: SdrProductLine[];
    notes: StagedSdrNote[];
    alternateAddress?: string;
    alternateEmail?: string;
    sdrId?: string;
    appointmentId?: string;
    statuscode?: SdrStatus;
  }): Promise<SdrCreateResult> {
    const sdrId = payload.sdrId ?? Guid.create().toString();
    const targetStatus = payload.statuscode ?? SdrStatus.Submitted;

    const sdrBody: Record<string, any> = {
      indskr_samplerequestid: sdrId,
      indskr_type: SDR_TYPE_DELIVERY_REQUEST,
      // Always created Active — the status-completion update below is what
      // actually finalizes it, as its own distinct batch operation.
      statuscode: SdrStatus.Active,
      indskr_externalid: `${SDR_EXTERNAL_ID_PREFIX}${sdrId}`,
    };
    if (payload.contactId) {
      sdrBody['indskr_Customer_contact@odata.bind'] = `/contacts(${payload.contactId})`;
    }
    const userId = this.authService.user?.systemUserID;
    if (userId) {
      sdrBody[SDR_USER_NAV] = `/systemusers(${userId})`;
    }
    if (payload.appointmentId) {
      sdrBody[SDR_APPOINTMENT_NAV] = `/appointments(${payload.appointmentId})`;
    }
    const altAddress = payload.alternateAddress?.trim();
    if (altAddress) {
      sdrBody['indskr_alternativeaddress'] = altAddress;
    } else if (payload.deliveryAddress) {
      sdrBody['indskr_Address@odata.bind'] = `/indskr_addresses(${payload.deliveryAddress})`;
    }
    const altEmail = payload.alternateEmail?.trim();
    if (altEmail) {
      sdrBody['indskr_alternativeemailaddress'] = altEmail;
    } else if (payload.email) {
      sdrBody['indskr_EmailAddress@odata.bind'] = `/indskr_email_addresses(${payload.email})`;
    }

    const products = payload.products ?? [];
    const notes = payload.notes ?? [];

    this.dynamics.startBatch({ returnRepresentation: true, includeAnnotations: '*' });
    try {
      this.dynamics.createRequest({ collection: SDR_ENTITY, data: sdrBody, returnRepresentation: true });

      for (const p of products) {
        const line: Record<string, any> = {
          'indskr_SampleRequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
          'indskr_SKU@odata.bind': `/products(${p.skuId})`,
          indskr_quantity: p.qty,
          // See addSdrLine's comment — indskr_requesttype, not indskr_type.
          indskr_requesttype: SDR_TYPE_DELIVERY_REQUEST,
        };
        if (p.productId) {
          line['indskr_Product@odata.bind'] = `/products(${p.productId})`;
        }
        this.dynamics.createRequest({ collection: LINE_ITEM_ENTITY, data: line, returnRepresentation: true });
      }

      for (const note of notes) {
        const body: Record<string, any> = {
          notetext: note.noteText ?? '',
          'objectid_indskr_samplerequest@odata.bind': `/${SDR_ENTITY}(${sdrId})`,
        };
        if (note.attachment) {
          body.documentbody = note.attachment.documentBody;
          body.filename = note.attachment.fileName;
          body.mimetype = note.attachment.mimeType;
          body.filesize = note.attachment.fileSize;
          body.isdocument = true;
        }
        this.dynamics.createRequest({ collection: ANNOTATIONS_ENTITY, data: body, returnRepresentation: true });
      }

      // Status completion — its own distinct batch operation, ALWAYS LAST,
      // only sent when the local doc is actually finalized. A still-Active
      // Draft synced for the first time (rep reconnected, hasn't tapped
      // Submit) stays Active — no completion op at all, same as createSdr's
      // existing Draft handling.
      // Ordering here is load-bearing, not cosmetic: Dynamics-side, a
      // Customer Allocation record is created off the indskr_samplereconciliation
      // (line item) create, but its quantity is only decremented by the
      // plugin/workflow that fires on the SDR header's statuscode transition
      // to Submitted — not on the line item create itself. Since every
      // request here shares one changeset (one transaction), the status
      // update must be queued after every line/note create so that
      // transition sees them already present when it fires.
      if (targetStatus === SdrStatus.Submitted) {
        this.dynamics.update(sdrId, SDR_ENTITY, { statuscode: SdrStatus.Submitted }, 'return=representation');
      }
    } catch (err) {
      this.dynamics.abortBatch();
      throw err;
    }

    const batchResults: any[] = await withTimeout(this.dynamics.executeBatch(), SDR_SUBMIT_TIMEOUT_MS);

    // Atomic: a rejection anywhere in the changeset means NOTHING committed.
    // dynamics-web-api never rejects executeBatch's own promise for a
    // sub-request failure — each failed sub-request instead comes back as
    // an Error INSTANCE at its position in the results array (see
    // parseBatchResponse) — so this must be checked explicitly.
    const firstError = batchResults.find((r) => r instanceof Error);
    if (firstError) {
      throw firstError;
    }

    const sdrRaw = batchResults[0];
    const sdr = new SampleDeliveryRequest(sdrRaw);
    if (payload.appointmentId) {
      sdr.appointmentId = payload.appointmentId;
    }

    // Atomic success ⇒ every line committed (a rejection would have thrown above).
    const lineResults: SdrLineItemResult[] = products.map((product) => ({ product, status: 'accepted' as const }));

    const notesResult: IONote[] = notes.map((stagedNote, i) => {
      const anno: any = batchResults[1 + products.length + i];
      return new IONote({
        annotationid: anno?.annotationid,
        notetext: anno?.notetext ?? stagedNote?.noteText ?? '',
        filename: anno?.filename ?? stagedNote?.attachment?.fileName ?? '',
        mimetype: anno?.mimetype ?? stagedNote?.attachment?.mimeType ?? '',
        filesize: anno?.filesize ?? stagedNote?.attachment?.fileSize ?? 0,
        isdocument: anno?.isdocument ?? !!stagedNote?.attachment,
        createdon: anno?.createdon,
        ownerName: anno?.['_ownerid_value@OData.Community.Display.V1.FormattedValue'] ?? this.authService.user?.displayName ?? '',
        ownerid: anno?._ownerid_value ?? this.authService.user?.systemUserID ?? '',
      });
    });

    sdr.products = products.map((p) => ({ ...p }));
    sdr.notes = notesResult;
    sdr.notesLoaded = true;
    if (targetStatus === SdrStatus.Submitted) {
      sdr.statusCode = SdrStatus.Submitted;
    }

    return { sdr, notes: notesResult, lineResults, failedNoteCount: 0 };
  }

  /**
   * Create an SDR locally while offline. Persists the EXACT payload `createSdr`
   * would send (`pendingFormPayload`) so `uploadPendingSdrs` replays it verbatim
   * on reconnect — no separate offline batch-building to drift from the online
   * path. Returns a hydrated SampleDeliveryRequest so the caller (business
   * service) can seed the display caches immediately, exactly as the online
   * create does, and the pending SDR shows in the meeting section + timeline
   * before it ever syncs.
   *
   * Storage mirrors the medical-inquiry offline convention verbatim
   * (`updateOrInsert(key, () => ({ raw }))` + `pendingSync` marker inside `raw`),
   * so the same replay/reconcile machinery reads it back identically.
   */
  createSdrOffline(payload: PendingSdrPayload): SampleDeliveryRequest {
    const createdAtIso = new Date().toISOString();
    const rawData: Record<string, any> = {
      indskr_samplerequestid: payload.sdrId,
      indskr_name: '',
      indskr_type: SDR_TYPE_DELIVERY_REQUEST,
      statuscode: SdrStatus.Submitted,
      // Offline has no server round-trip to resolve the optionset's display
      // label, so without this the Status field renders blank in the UI
      // despite statuscode above being correctly set (OMNI-61543).
      'statuscode@OData.Community.Display.V1.FormattedValue': this.translate.instant('SUBMITTED'),
      indskr_externalid: `${SDR_EXTERNAL_ID_PREFIX}${payload.sdrId}`,
      _indskr_customer_value: payload.contactId,
      '_indskr_customer_value@OData.Community.Display.V1.FormattedValue': payload.contactName ?? '',
      _indskr_appointment_value: payload.appointmentId ?? '',
      // Saved-address/email lookup ids + their display names. Both are '' when
      // the rep used the free-text alternate instead (payload.deliveryAddress /
      // payload.email are already blanked by submitOffline()'s state mapping in
      // that case) — displayDeliveryAddress/displayEmailAddress prefer
      // alternateAddress/alternateEmail over these anyway.
      _indskr_address_value: payload.deliveryAddress ?? '',
      '_indskr_address_value@OData.Community.Display.V1.FormattedValue': payload.deliveryAddressLabel ?? '',
      _indskr_emailaddress_value: payload.email ?? '',
      '_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue': payload.emailLabel ?? '',
      indskr_alternativeaddress: payload.alternateAddress ?? '',
      indskr_alternativeemailaddress: payload.alternateEmail ?? '',
      createdon: createdAtIso,
      // Offline-replay bookkeeping — read back by uploadPendingSdrs().
      pendingSync: true,
      pendingOperation: 'create',
      pendingFormPayload: payload,
    };

    const sdr = new SampleDeliveryRequest(rawData);
    sdr.products = (payload.products ?? []).map((p) => ({ ...p }));
    sdr.notes = (payload.notes ?? []).map((note, idx) =>
      new IONote({
        annotationid: `offline-note-${payload.sdrId}-${idx}`,
        notetext: note.noteText ?? '',
        filename: note.attachment?.fileName ?? '',
        mimetype: note.attachment?.mimeType ?? '',
        filesize: note.attachment?.fileSize ?? 0,
        isdocument: !!note.attachment,
        createdon: createdAtIso,
        ownerName: this.authService.user?.displayName ?? '',
        ownerid: this.authService.user?.systemUserID ?? '',
      }),
    );
    // Built in hand, so treat as fully hydrated for the detail view (no re-fetch).
    sdr.notesLoaded = true;

    this.disk.updateOrInsert(`${DB_KEY_PREFIXES.SDR_RECORD}${payload.sdrId}`, () => ({ raw: rawData }));
    this.disk.addOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.SAMPLE_DELIVERY_REQUEST, 1);
    return sdr;
  }

  /**
   * Replay pending offline SDRs on reconnect. Each is independent:
   *  - RESOLVE the appointment link: if it's still a temp `offline_meeting_...` id,
   *    try to swap in the now-synced real Dynamics GUID via resolveAppointmentRef
   *    (meeting upload runs before uploadPendingSdrs in master.data.service, so the
   *    in-memory appointment's `.ID` has already been swapped by the time we get
   *    here). DEFER if it's still unresolved — the meeting itself hasn't synced
   *    yet — leave it pending and retry next cycle, so an SDR is never posted
   *    unlinked from its meeting.
   *  - RECONCILE before posting: the SDR id is client-generated and embedded in
   *    the payload, so a point lookup settles whether an earlier replay already
   *    committed it (response leg dropped after the server committed). Found =>
   *    clear the pending doc, no re-post. Not found => post. Mirrors
   *    MedicalInquiryDataService.uploadPendingMedicalInquiries.
   */
  async uploadPendingSdrs(): Promise<void> {
    if (this.device?.isOffline) {
      console.log('[SDR-SYNC] uploadPendingSdrs: device offline, skipping');
      return;
    }
    try {
      const docs = (await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_SDR_RECORDS, true, true)) as Array<{ _id?: string; raw?: Record<string, any> }>;
      // Push a still-being-edited Draft's HEADER too (statuscode Active, from
      // createSdrDraftOffline), matching Allocation Order's offline-upload
      // behavior (activity.service.ts's uploadOfflineMeetingsTimeOffs...
      // pushes any offline-created order regardless of open/completed state,
      // excluding only one created-and-canceled entirely offline). Submitting
      // is still a separate, later action — the create-below and the
      // reconcile branch's statuscode patch are both careful to only ever
      // move a header to Submitted when THIS local doc says Submitted, never
      // just because its header happens to sync while still Active.
      const pending = (docs || []).filter(
        (d) =>
          d?.raw &&
          d.raw.pendingSync === true &&
          (d.raw.statuscode === SdrStatus.Submitted || d.raw.statuscode === SdrStatus.Active),
      );
      console.log(`[SDR-SYNC] uploadPendingSdrs: ${docs?.length ?? 0} sdr_record_ docs on device, ${pending.length} pending upload`);
      if (!pending.length) return;

      let uploaded = 0;
      for (const doc of pending) {
        const payload = doc.raw!.pendingFormPayload as PendingSdrPayload;
        if (!payload?.sdrId) {
          let rawDump: string;
          try {
            rawDump = JSON.stringify(doc.raw);
          } catch {
            rawDump = String(doc.raw);
          }
          console.error(`[SDR-SYNC] uploadPendingSdrs: skipping ${doc._id} — no pendingFormPayload.sdrId. raw doc = ${rawDump}`);
          continue;
        }
        const key = `${DB_KEY_PREFIXES.SDR_RECORD}${payload.sdrId}`;
        console.log(`[SDR-SYNC] uploadPendingSdrs: attempting ${payload.sdrId} appointmentId=${payload.appointmentId} products=${payload.products?.length ?? 0}`);

        let appointmentId = payload.appointmentId;
        if (appointmentId && appointmentId.includes('offline')) {
          appointmentId = this.resolveAppointmentRef(appointmentId);
          // Defer: the meeting was created offline and hasn't synced yet — its real
          // appointment id doesn't exist server-side, so linking would fail.
          if (!appointmentId) {
            console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} deferred, meeting ${payload.appointmentId} not yet synced`);
            continue;
          }
        }
        try {
          // Reconcile: has the header already committed (an earlier replay's
          // dropped response, or an append-lines retry)? If so, create only the
          // lines not yet present — dedup by SKU (unique per SDR via
          // isSkuInForm) so a line that committed-but-dropped never duplicates —
          // plus any notes still carried in the payload (see below).
          // Otherwise it's a fresh replay: create header + lines + notes.
          // The reconcile lookup MUST distinguish "header not found" from "the
          // lookup itself failed". Swallowing a thrown lookup into null (the old
          // `.catch(() => null)`) treated a transient network blip as "not
          // committed" → re-posted the header with the same client PK → dup-key
          // 400 → a spurious rejection notification for an SDR that was actually
          // fine, plus its uncommitted lines lost. So: a THROW defers this doc
          // to the next cycle (leave pending, don't re-post); only a genuine
          // empty result takes the fresh-create path.
          let existing: SampleDeliveryRequest | null;
          try {
            existing = await this.fetchSdrById(payload.sdrId);
          } catch (lookupErr) {
            console.error(`[SDR-SYNC] uploadPendingSdrs: reconcile lookup failed for ${payload.sdrId}, deferring`, lookupErr);
            continue;
          }
          let lineResults: SdrLineItemResult[];
          // Tracks the now-server-confirmed record so a full success below can
          // re-save it as synced (pendingSync: false) instead of bare-removing
          // its doc — removing would make the SDR vanish from the offline
          // list/meeting view the instant it syncs, before the next
          // syncRepSdrs/fetchSdrsForMeeting has a chance to replace it with the
          // server-hydrated version.
          let syncedSdr: SampleDeliveryRequest | null = existing;
          // Ids that were queued for delete (queueSdrLineDeleteOffline/
          // queueSdrNoteDeleteOffline) but still failed THIS cycle too — read
          // by the transient/full-success check further below so a doc with a
          // stuck delete is never marked fully synced (pendingSync: false)
          // while that delete is still outstanding server-side.
          let stillPendingLineDeleteIds: string[] = [];
          let stillPendingNoteDeleteIds: string[] = [];
          if (existing) {
            // Apply any queued offline deletes for lines/notes THIS SDR
            // already had synced before the rep removed them offline — MUST
            // run before existingSkus/missing below are computed, and MUST
            // mutate `existing.products`/`existing.notes` in place so those
            // computations see the post-delete state. Otherwise a delete
            // immediately followed by a re-add of the same SKU (same
            // contact+SKU, different qty) would see the old line as "still
            // existing" and silently skip creating the replacement — the
            // product would just vanish instead of ending up at the new qty.
            // Independent Promise.allSettled calls, same rationale as
            // _createSdrLines/_createSdrNotes — one failed delete must not
            // block the others, or the header patch/missing-line creates
            // below.
            const lineDeleteIds = payload.pendingLineDeleteIds ?? [];
            const noteDeleteIds = payload.pendingNoteDeleteIds ?? [];
            if (lineDeleteIds.length || noteDeleteIds.length) {
              const [lineDeleteOutcomes, noteDeleteOutcomes] = await Promise.all([
                Promise.allSettled(lineDeleteIds.map((id) => this.removeSdrLine(id))),
                Promise.allSettled(noteDeleteIds.map((id) => this.removeSdrNote(id))),
              ]);
              const deletedLineIds = new Set<string>();
              lineDeleteOutcomes.forEach((outcome, i) => {
                const id = lineDeleteIds[i];
                if (outcome.status === 'fulfilled') {
                  deletedLineIds.add(id);
                } else {
                  stillPendingLineDeleteIds.push(id);
                  console.error(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} queued line delete FAILED for lineId=${id}, deferring`, outcome.reason);
                }
              });
              const deletedNoteIds = new Set<string>();
              noteDeleteOutcomes.forEach((outcome, i) => {
                const id = noteDeleteIds[i];
                if (outcome.status === 'fulfilled') {
                  deletedNoteIds.add(id);
                } else {
                  stillPendingNoteDeleteIds.push(id);
                  console.error(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} queued note delete FAILED for annotationId=${id}, deferring`, outcome.reason);
                }
              });
              if (deletedLineIds.size) {
                existing.products = (existing.products ?? []).filter((p) => !deletedLineIds.has(p.lineId ?? ''));
              }
              if (deletedNoteIds.size) {
                existing.notes = (existing.notes ?? []).filter((n) => !deletedNoteIds.has(n.noteId));
              }
              console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} processed queued deletes — lines deleted=${deletedLineIds.size}/${lineDeleteIds.length}, notes deleted=${deletedNoteIds.size}/${noteDeleteIds.length}`);
              // Carry only the still-failed ids forward — whatever persists
              // `payload` below (the transient/partial branch further down)
              // must not re-attempt an already-succeeded delete next cycle.
              payload.pendingLineDeleteIds = stillPendingLineDeleteIds;
              payload.pendingNoteDeleteIds = stillPendingNoteDeleteIds;
            }
            // The header may still be sitting at Active server-side (created
            // ONLINE via createSdrDraft, or offline via createSdrDraftOffline
            // and reconciled by a PRIOR cycle) while the rep has since tapped
            // Submit locally and/or added more products/notes/contact fields
            // offline — finalizeSdr's offline branch can only flip the LOCAL
            // doc's statuscode, it has no server round-trip to reach while
            // offline. All of that gets reconciled here as ONE Dynamics
            // $batch (contact/address/email patch, still-missing line
            // creates, note creates, and — only when the local doc is
            // actually Submitted — the status-completion update, QUEUED
            // LAST) instead of up to four independent sequential calls.
            // Queuing the status update last is load-bearing, not cosmetic —
            // see _reconcileSdrBatch's own doc comment (OMNI-61561): a status
            // update sent BEFORE the still-missing lines makes Dynamics'
            // line-item plugin reject those lines outright, since the header
            // is no longer Active by the time they're created.
            const contactPatch = this.buildContactFieldsPatch({
              contactId: payload.contactId,
              deliveryAddress: payload.deliveryAddress,
              email: payload.email,
              alternateAddress: payload.alternateAddress,
              alternateEmail: payload.alternateEmail,
            });
            const existingSkus = new Set((existing.products ?? []).map((p) => p.skuId));
            const missing = (payload.products ?? []).filter((p) => !existingSkus.has(p.skuId));

            const reconciled = await this._reconcileSdrBatch({
              sdrId: payload.sdrId,
              existingStatusCode: existing.statusCode,
              localStatuscode: doc.raw!.statuscode,
              contactPatch: Object.keys(contactPatch).length ? contactPatch : undefined,
              missingProducts: missing,
              notes: payload.notes ?? [],
            });

            lineResults = reconciled.lineResults;
            // Reflect the patch on the in-memory record too — syncedSdr is set
            // to this same `existing` object below and, on full success, gets
            // saved back to the local doc via saveSdrRecordToDb. Without this
            // syncedSdr would still carry the pre-patch Active value fetched a
            // moment ago, silently reverting the local device's own belief
            // that the SDR was submitted even though the server patch just
            // succeeded.
            if (reconciled.statusPatched) {
              existing.statusCode = SdrStatus.Submitted;
            }
            // Merge in whatever newly committed this cycle — existing.products/
            // notes only reflect what fetchSdrById saw BEFORE this batch ran.
            existing.products = [
              ...(existing.products ?? []),
              ...reconciled.lineResults.filter((r) => r.status === 'accepted').map((r) => r.product),
            ];
            existing.notes = [...(existing.notes ?? []), ...reconciled.notes];
          } else {
            // Create the header AS the local doc's own statuscode — Active
            // for a still-unfinalized Draft reaching Dynamics for the first
            // time (rep reconnected, hasn't tapped Submit), Submitted for one
            // finalized entirely offline. Passed explicitly, same rationale
            // as createSdr's own callers — this is what prevents a Draft
            // from being silently auto-submitted just because its header
            // happened to sync before the rep finished composing it.
            //
            // Uses the batched offline-sync path — header create +
            // reconciliation (line) creates + annotation creates + a
            // separate status-completion update, all as ONE Dynamics
            // $batch — instead of createSdr's independent per-call
            // sequence. See _createSdrBatch's own doc comment for why this
            // path is deliberately atomic where createSdr deliberately is
            // not.
            const result = await this._createSdrBatch({ ...payload, appointmentId, statuscode: doc.raw!.statuscode });
            lineResults = result.lineResults;
            syncedSdr = result.sdr;
          }

          console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} header committed. Line results: ${JSON.stringify(lineResults.map((r) => ({ sku: r.product?.skuId, status: r.status, kind: r.kind, error: r.errorMessage })))}`);
          const rejected = lineResults.filter((r) => r.status === 'rejected');
          const transient = rejected.filter((r) => r.kind === 'transient');
          const deterministic = rejected.filter((r) => r.kind === 'deterministic');

          // Deterministic rejections are terminal — surface them (retrying can't
          // change the verdict). Fired even when transient lines remain below,
          // since the retry payload drops the deterministic ones (they won't be
          // re-attempted, so this is the only chance to notify).
          if (deterministic.length) {
            this._notifySdrLineRejections(payload, deterministic);
          }

          if (transient.length || stillPendingLineDeleteIds.length || stillPendingNoteDeleteIds.length) {
            // Keep pending: retry the transient lines AND any still-failed
            // queued deletes next cycle, against the now-existing header
            // (append-lines mode). Accepted + deterministically-rejected
            // lines drop out of the payload. Notes are cleared too: whichever
            // branch ran above already created them this cycle, so carrying
            // them forward would double-create on the next append-lines
            // pass. pendingLineDeleteIds/pendingNoteDeleteIds come through
            // via the `...payload` spread — already narrowed to just the
            // still-failed ids above, so a SUCCEEDED delete is never
            // re-attempted next cycle just because an unrelated line create
            // was transient this time.
            const remaining: PendingSdrPayload = { ...payload, products: transient.map((r) => r.product), notes: [] };
            await this.disk.updateOrInsert(key, () => ({
              raw: { ...doc.raw, pendingFormPayload: remaining, pendingOperation: 'append-lines' },
            }));
            // Not counted as uploaded — the doc is still pending.
            console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} partial — ${transient.length} line(s), ${stillPendingLineDeleteIds.length} line delete(s), ${stillPendingNoteDeleteIds.length} note delete(s) deferred for retry`);
          } else if (syncedSdr) {
            // Full success: re-save as a normal synced record (same doc, same
            // key) rather than removing it — see the comment on syncedSdr above.
            syncedSdr.pendingSync = false;
            await this.saveSdrRecordToDb(syncedSdr);
            uploaded++;
            console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} uploaded to Dynamics successfully, server id=${syncedSdr.id}`);
            // Let the business service's in-memory allSdrs pick up the
            // server-hydrated record immediately — see _sdrSynced$'s own doc
            // comment for why this can't wait for a reopened meeting or the
            // separate syncRepSdrs pipeline task.
            this._sdrSynced$.next(syncedSdr);
          } else {
            // Shouldn't happen (both branches above always set syncedSdr on
            // success), but fall back to the old remove-only behavior rather
            // than leaving a synced record with no doc at all.
            await this.disk.remove(key);
            uploaded++;
          }
        } catch (err) {
          // Only the header create throws here (line/note creates use
          // Promise.allSettled and never reject) — EXCEPT for the fresh-create
          // branch above, which now goes through _createSdrBatch: since that
          // batch is one atomic changeset, ANY rejected op inside it (header,
          // any line, any note, or the status-completion update) throws here
          // too. Classify it:
          const kind = classifySdrFailure(err);
          if (kind === 'deterministic') {
            // A quantity-shaped rejection is potentially self-correcting: the
            // batch was atomic, so nothing committed, but the payload itself
            // can be repaired and retried rather than dropped outright. Only
            // meaningful for the fresh-create (_createSdrBatch) path — the
            // reconcile/append-lines branch above already isolates rejections
            // per-line via Promise.allSettled and never needed this.
            if (this._looksQuantityRelatedSdrRejection(err)) {
              try {
                const capped = await this._capSdrPayloadToAvailableAllocation(payload);
                if (capped.changed) {
                  if (capped.products.length) {
                    await this.disk.updateOrInsert(key, () => ({
                      raw: { ...doc.raw, pendingFormPayload: { ...payload, products: capped.products } },
                    }));
                    console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} heuristic-capped to available allocation, left pending for retry`);
                  } else {
                    await this.disk.remove(key);
                    uploaded++;
                    console.log(`[SDR-SYNC] uploadPendingSdrs: ${payload.sdrId} every line capped to 0 available — dropped`);
                  }
                  continue;
                }
                // capped.changed === false: refetch found nothing over limit —
                // the heuristic can't explain this rejection; fall through to
                // the generic header-rejection handling below.
              } catch (capErr) {
                console.error(`[SDR-SYNC] uploadPendingSdrs: heuristic cap attempt failed for ${payload.sdrId}`, capErr);
              }
            }
            // The header itself was rejected (e.g. create-privilege / validation)
            // — it will never commit, so stop retrying (previously this retried
            // forever) and surface it to the rep.
            console.error(`[SDR-SYNC] uploadPendingSdrs: header REJECTED (deterministic) for ${payload.sdrId}, giving up`, err);
            this._notifySdrHeaderRejection(payload, err);
            await this.disk.remove(key);
            uploaded++;
          } else {
            // Transient (timeout / 5xx / network) — leave pending, retry next cycle.
            console.error(`[SDR-SYNC] uploadPendingSdrs: deferred (transient) for ${payload.sdrId}`, err);
          }
        }
      }
      if (uploaded > 0) {
        this.disk.subtractOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.SAMPLE_DELIVERY_REQUEST, uploaded);
      }
    } catch (err) {
      console.error('[SDR-SYNC] uploadPendingSdrs: failed', err);
    }
  }

  /**
   * Matches the rejection message against the one plugin text known to be
   * fixable by lowering a quantity. "No Allocation Config found." and
   * "Customer is not eligible for Sample Request for this SKU." (the other
   * two documented PreOperationSRLineItem rejections) are deliberately
   * excluded — no quantity change fixes either, so there's no point running
   * the refetch-and-cap heuristic below for them.
   */
  private _looksQuantityRelatedSdrRejection(err: any): boolean {
    const message = String(err?.message ?? err?.error?.message ?? '').toLowerCase();
    return message.includes('limit exceeded');
  }

  /**
   * Refetch actual remaining allocation per SKU in the payload and cap any
   * line whose qty now exceeds it (dropping the line entirely if capped to
   * 0). Deliberately a heuristic over EVERY line in the payload, not an
   * attempt to identify Dynamics' one true offending sub-request: the
   * atomic batch's rollback can return error responses for innocent sibling
   * lines too, so there is no reliable way to isolate just the real
   * offender from the batch response alone.
   */
  private async _capSdrPayloadToAvailableAllocation(
    payload: PendingSdrPayload,
  ): Promise<{ changed: boolean; products: SdrProductLine[] }> {
    const skuIds = (payload.products ?? []).map((p) => p.skuId).filter(Boolean);
    if (!payload.contactId || !skuIds.length) {
      return { changed: false, products: payload.products ?? [] };
    }
    const allocations = await this.fetchManualAllocationsForContact(payload.contactId, skuIds);
    const bySku = new Map(allocations.map((a) => [a.skuId.toLowerCase(), a]));
    let changed = false;
    const products = (payload.products ?? [])
      .map((p) => {
        const alloc = bySku.get(p.skuId?.toLowerCase());
        if (!alloc || alloc.isUnlimited || p.qty <= alloc.remaining) return p;
        changed = true;
        const cappedQty = Math.max(0, alloc.remaining);
        return cappedQty > 0 ? { ...p, qty: cappedQty } : null;
      })
      .filter((p): p is SdrProductLine => p !== null);
    return { changed, products };
  }

  /**
   * Durable in-app notification that some product line(s) of an offline SDR were
   * rejected by Dynamics on replay (deterministic — retrying won't help). The
   * offline submit already reported "saved, will sync", and the next pull sync
   * silently corrects the product list, so without this the rep would never
   * learn a requested SKU didn't go through. Mirrors the DATA_UPLOAD_ERR_MEETING
   * / _ALLOCATION offline-upload-failure notifications (activity.service.ts).
   */
  private _notifySdrLineRejections(payload: PendingSdrPayload, rejected: SdrLineItemResult[]): void {
    try {
      const products = rejected
        .map((r) => `${r.product.skuName}${r.errorMessage ? `: ${r.errorMessage}` : ''}`)
        .join('; ');
      const contact = payload.contactName || '';
      const params = { contact, products };
      const model: IndNotificationDataModel = {
        type: NOTIFICATION.DATA_UPLOAD_ERR_SDR,
        name: this.translate.instant('DATA_UPLOAD_ERR_SDR_PRODUCTS', params),
        DateTime: new Date().getTime(),
        id: NOTIFICATION.DATA_UPLOAD_ERR_SDR + payload.sdrId,
        data: { sdrId: payload.sdrId },
        icon: 'assets/imgs/sample-delivery-request-icon.svg',
        isRed: false,
        params,
      };
      this.myAssistantService.saveNotificationToDisk(model);
    } catch (err) {
      console.error('SampleDeliveryRequestDataService: _notifySdrLineRejections failed', err);
    }
  }

  /**
   * Durable in-app notification that an offline SDR's header itself was
   * deterministically rejected on replay (e.g. create-privilege / validation) —
   * the whole request will never commit, so it's dropped from the replay queue
   * and surfaced here rather than retried forever.
   */
  private _notifySdrHeaderRejection(payload: PendingSdrPayload, err: any): void {
    try {
      const reason = err?.message || err?.error?.message || this.translate.instant('SDR_REJECTED_GENERIC_REASON');
      const contact = payload.contactName || '';
      const params = { contact, reason };
      const model: IndNotificationDataModel = {
        type: NOTIFICATION.DATA_UPLOAD_ERR_SDR,
        name: this.translate.instant('DATA_UPLOAD_ERR_SDR_HEADER', params),
        DateTime: new Date().getTime(),
        id: NOTIFICATION.DATA_UPLOAD_ERR_SDR + payload.sdrId,
        data: { sdrId: payload.sdrId },
        icon: 'assets/imgs/sample-delivery-request-icon.svg',
        isRed: false,
        params,
      };
      this.myAssistantService.saveNotificationToDisk(model);
    } catch (e) {
      console.error('SampleDeliveryRequestDataService: _notifySdrHeaderRejection failed', e);
    }
  }

  /**
   * Patch an existing annotation. Detail-view only (create-form notes are
   * in-memory until the submit batch). Accepts whatever shape the caller wants
   * to send — text-only edits, attachment swaps, and attachment removals all
   * share this entry point.
   */
  updateNote(annotationId: string, patch: Record<string, any>): Promise<any> {
    return this.dynamics.update(annotationId, ANNOTATIONS_ENTITY, patch);
  }

  deleteNote(annotationId: string): Promise<any> {
    return this.dynamics.delete(annotationId, ANNOTATIONS_ENTITY);
  }

  /**
   * Fetch the contact's SDRs as lightweight TIMELINE SUMMARIES: only the fields
   * the timeline card displays and the timeline filters need — title, status,
   * delivery address, email, createdOn, and product names + ids. Notes, contact,
   * and product quantities are intentionally NOT fetched here; the detail view
   * pulls the full record on open via `fetchSdrById`.
   *
   * Product lines are `indskr_samplereconciliation` records linked back to the
   * SDR through the `indskr_SampleRequest` lookup (the same one each line item
   * create binds via `indskr_SampleRequest@odata.bind` — see createSdr). The
   * product name comes from the `indskr_product` lookup's FormattedValue, so
   * no extra `product`-entity join is needed.
   *
   * The product join is outer and denormalised (one row per (SDR, product));
   * `groupSdrRows` groups by SDR id and de-dupes products by reconciliation id.
   */
  async fetchSdrsForContact(contactId: string): Promise<SampleDeliveryRequest[]> {
    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchByContactTimeline
      .replace('{contactId}', contactId)
      .replace('{sdrType}', String(SDR_TYPE_DELIVERY_REQUEST));

    const response = await this.dynamics.executeFetchXml(SDR_ENTITY, fetchXml);
    return this.groupSdrRows(response?.value ?? []);
  }

  /**
   * Fetch the SDRs raised from a given meeting (OMNI-61002), as timeline-style
   * summaries grouped by `groupSdrRows`. Mirrors `fetchSdrsForContact` but
   * filters on the appointment lookup instead of the customer.
   *
   * DEPENDS ON OMNI-61003: the `fetchByAppointment` query filters on the
   * appointment lookup field that ticket adds to `indskr_samplerequest`. Until
   * it lands server-side this returns []; the offline/cache path
   * (SampleDeliveryRequestService.fetchSdrsForMeeting) still surfaces pending
   * SDRs without it.
   */
  async fetchSdrsForAppointment(appointmentId: string): Promise<SampleDeliveryRequest[]> {
    if (!appointmentId) return [];
    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchByAppointment
      .replace('{appointmentId}', appointmentId)
      .replace('{sdrType}', String(SDR_TYPE_DELIVERY_REQUEST));

    const response = await this.dynamics.executeFetchXml(SDR_ENTITY, fetchXml);
    return this.groupSdrRows(response?.value ?? []);
  }

  /**
   * Full SDR fetch for the detail view: customer, delivery address, email,
   * status, product lines WITH quantities, and notes. documentbody is NOT
   * requested — io-note pulls files on demand through
   * IoFileService.getDocumentBody(annotationid) on download. Returns null when
   * the id is not found.
   *
   * Both child joins are outer and denormalised, so each row is one
   * (SDR, annotation, product) combination — a cartesian product across the two
   * children. `groupSdrRows` de-dupes notes and products by their own ids.
   */
  async fetchSdrById(sdrId: string): Promise<SampleDeliveryRequest | null> {
    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchById.replace('{sdrId}', sdrId);

    const response = await this.dynamics.executeFetchXml(SDR_ENTITY, fetchXml);
    const grouped = this.groupSdrRows(response?.value ?? []);
    const sdr = grouped[0] ?? null;
    // Full detail (notes included, even if zero) — mark hydrated so the detail
    // view's offline gate knows it never needs to re-fetch/read-cache this one.
    if (sdr) sdr.notesLoaded = true;
    return sdr;
  }

  /**
   * Group denormalised SDR fetch rows (one row per (SDR, annotation, product)
   * combination) into SampleDeliveryRequest objects, de-duping notes by
   * annotationid and products by reconciliation id. Works for both the slim
   * timeline query (no note rows; product qty absent → 0) and the full
   * `fetchSdrById` query.
   */
  private groupSdrRows(rows: any[]): SampleDeliveryRequest[] {
    const grouped = new Map<string, SampleDeliveryRequest>();
    const seenNotes = new Map<string, Set<string>>();
    const seenProducts = new Map<string, Set<string>>();
    for (const row of rows) {
      const id = row['indskr_samplerequestid'];
      if (!id) continue;
      let sdr = grouped.get(id);
      if (!sdr) {
        sdr = new SampleDeliveryRequest(row);
        grouped.set(id, sdr);
        seenNotes.set(id, new Set());
        seenProducts.set(id, new Set());
      }

      const annotationId = row['note.annotationid'];
      if (annotationId && !seenNotes.get(id).has(annotationId)) {
        seenNotes.get(id).add(annotationId);
        sdr.notes.push(
          new IONote({
            annotationid: annotationId,
            notetext: row['note.notetext'] ?? '',
            filename: row['note.filename'] ?? '',
            mimetype: row['note.mimetype'] ?? '',
            filesize: row['note.filesize'] ?? 0,
            isdocument: !!row['note.isdocument'],
            createdon: row['note.createdon'],
            // Note creator — so the detail view shows who wrote the note (parity
            // with meetings). The owner lookup's FormattedValue is the display
            // name; the raw value is the systemuser id.
            ownerName: row['note.ownerid@OData.Community.Display.V1.FormattedValue'] ?? '',
            ownerid: row['note.ownerid'] ?? '',
          }),
        );
      }

      const reconciliationId = row['prod.indskr_samplereconciliationid'];
      if (reconciliationId && !seenProducts.get(id).has(reconciliationId)) {
        seenProducts.get(id).add(reconciliationId);
        // Fall back to indskr_product for records created before indskr_sku was written.
        sdr.products.push({
          skuId: row['prod.indskr_sku'] ?? row['prod.indskr_product'] ?? '',
          skuName:
            row['prod.indskr_sku@OData.Community.Display.V1.FormattedValue'] ??
            row['prod.indskr_product@OData.Community.Display.V1.FormattedValue'] ??
            '',
          productId: row['prod.indskr_product'] ?? '',
          productName:
            row['prod.indskr_product@OData.Community.Display.V1.FormattedValue'] ?? '',
          lineId: reconciliationId,
          qty: row['prod.indskr_quantity'] ?? 0,
          minQty: 0,
          maxQty: null,
        });
      }
    }
    return Array.from(grouped.values());
  }

  /**
   * Fetch the rep's OWN SDRs (owner-scoped) as timeline summaries incl. product
   * qty and the customer lookup. Powers the device-wide offline cache. No notes.
   */
  async fetchRepSdrs(): Promise<SampleDeliveryRequest[]> {
    const ownerId = this.authService.user?.systemUserID ?? '';
    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchAllForRep
      .replace('{ownerId}', ownerId)
      .replace('{sdrType}', String(SDR_TYPE_DELIVERY_REQUEST));
    const response = await this.dynamics.executeFetchXml(SDR_ENTITY, fetchXml);
    return this.groupSdrRows(response?.value ?? []);
  }

  /**
   * Persist one SDR as its own doc. Only the record that actually changed is
   * written, mirroring MedicalInquiryDataService.saveMedicalInquiriesToDb's
   * per-record loop.
   */
  async saveSdrRecordToDb(sdr: SampleDeliveryRequest): Promise<void> {
    if (!sdr?.id) return;
    await this.disk.updateOrInsert(DB_KEY_PREFIXES.SDR_RECORD + sdr.id, (doc: any) => {
      if (!doc || !doc.raw) doc = { raw: null };
      const prev = doc.raw;
      // Never let a SLIM record blank out notes already captured on disk.
      // fetchAllForRep (syncRepSdrs) and fetchByAppointment join no annotations,
      // so their records carry notes: [] / notesLoaded: false. Writing one
      // straight over the stored doc discarded whatever _hydrateNotesIfNeeded
      // had lazily persisted, so after a delta sync the offline read served an
      // empty Notes section for a record whose notes were never actually lost
      // (OMNI-61595). Only the hydrated side may overwrite notes.
      const incomingIsSlim = !sdr.notesLoaded && !(sdr.notes?.length);
      const storedIsHydrated = !!prev?.notesLoaded && !!prev?.notes?.length;
      doc.raw = incomingIsSlim && storedIsHydrated
        ? { ...sdr, notes: prev.notes, notesLoaded: true }
        : sdr;
      return doc;
    });
  }

  /** Read one cached SDR record by id (null if absent) — convenience wrapper over the per-record store. */
  async getCachedSdrRecord(sdrId: string): Promise<SampleDeliveryRequest | null> {
    const doc = await this.disk.retrieve(DB_KEY_PREFIXES.SDR_RECORD + sdrId, true);
    return doc?.raw ? SampleDeliveryRequest.fromCached(doc.raw) : null;
  }

  /**
   * Load every SDR record via one prefix-range batchFetch. Mirrors
   * MedicalInquiryDataService.loadMedicalInquiriesFromDb.
   *
   * Also RECOMPUTES the SAMPLE_DELIVERY_REQUEST offline-data count from what's
   * actually persisted, mirroring DiskService.loadOfflineEntity (used by
   * loadOfflineMeetings/loadOfflineSampleOrders) — unlike SDR's own create/
   * finalize paths, which only ever increment/decrement that count via
   * addOfflineDataCount/subtractOfflineDataCount, those Allocation
   * Order/Meeting counts are self-healing because their loader recomputes
   * from disk on every call. SDR had no equivalent, so the count (used by
   * DiskService.isThereOfflineDataToBeUploaded, which the app's reconnect
   * handler trusts blindly — home.ts's networkReconnection/
   * userStatusBackToOnline case) stayed wherever the in-memory hashmap last
   * was, permanently 0 after any fresh app process start. Kill the app with
   * an SDR still pending upload, relaunch (offline or online), then reconnect
   * without another restart: the reconnect handler saw count 0 and skipped
   * the entire upload pass — SDR silently never reached Dynamics, with no
   * error surfaced anywhere (OMNI-61497 follow-up). This method already runs
   * at app startup via SampleDeliveryRequestService.syncRepSdrs(), the same
   * natural place Allocation Order's equivalent recompute runs from
   * (loadActivitiesFromDBAndMap, called unconditionally whether online or
   * offline), so no separate startup call needed.
   */
  async loadAllSdrRecordsFromDb(): Promise<SampleDeliveryRequest[]> {
    const docs = (await this.disk.batchFetch(DB_ALLDOCS_QUERY_OPTIONS.GET_ALL_SDR_RECORDS, true, true)) as Array<{ _id?: string; raw?: any }>;
    const sdrs = (docs || [])
      .map((d) => d?.raw)
      .filter(Boolean)
      .map((raw: any) => SampleDeliveryRequest.fromCached(raw));
    const pendingCount = sdrs.filter((s) => s.pendingSync).length;
    this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.SAMPLE_DELIVERY_REQUEST, pendingCount);
    return sdrs;
  }

  /** Remove one SDR's record doc (e.g. dropped by a delta sync, or a deterministic offline-replay rejection). */
  async deleteSdrRecordFromDb(sdrId: string): Promise<void> {
    await this.disk.remove(DB_KEY_PREFIXES.SDR_RECORD + sdrId);
  }

  /**
   * Fetch every currently-valid Manual-Distribution Allocation Config,
   * outer-joined to the contact's Customer Allocation record if one already
   * exists AND outer-joined (through the Allocation↔Contact N:N) to confirm
   * whether this contact is actually associated — the eligibility ceiling
   * AND eligibility signal behind the SDR limit-validation client-side
   * pre-check. Deliberately isolated from the Sample Drop feature's own
   * Customer Allocation sync
   * (SampleDataService._fetchAndSaveCustomerSampleAllocations): a fresh,
   * on-demand, per-contact FetchXML rather than reusing that device-wide,
   * near-term-windowed sync. Rooted at the Config (not Customer Allocation)
   * so a row still comes back on a first-ever request — see the FetchXML's
   * own comment in dynamics-fetchQueries.ts for the full design.
   */
  async fetchManualAllocationsForContact(contactId: string, skuIds: string[]): Promise<SdrCustomerAllocation[]> {
    if (!skuIds?.length) return [];
    // {contactId} appears twice (the "ca" Customer Allocation filter and the
    // "aa" eligibility-join filter) — a global regex is required, a single
    // .replace() only substitutes the first occurrence and leaves the second
    // as a literal "{contactId}", which Dynamics then fails to parse as a GUID.
    let sampleProductIdsXml = '';
    skuIds.forEach((id) => { sampleProductIdsXml += `<value>${id}</value>`; });
    // Today, matching GetAllocationConfiguration. Dynamics wants YYYY-MM-DD.
    const compareToDate = this.dateTimeFormatsService.formatDate(new Date(), 'YYYY-MM-DD');
    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsForContact
      .replace(/\{contactId\}/g, contactId)
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace('{compareToDate}', compareToDate);
    const response = await this.dynamics.executeFetchXml<any>(ALLOCATION_CONFIG_ENTITY, fetchXml);
    return (response?.value ?? []).map((row: any) => {
      // The outer-joined alias's own PK is null when no Customer Allocation
      // record exists yet for this contact+SKU — that's the "first request"
      // case, where the ceiling is the config's own untouched limit rather
      // than a consumption-tracking field that doesn't exist yet.
      const customerAllocationId: string | undefined = row['ca.indskr_customersampleproductid'] ?? undefined;
      const limit = row['indskr_eligibilitylimit'] ?? 0;
      return {
        id: customerAllocationId,
        // Lookup/FK attributes come back from Dynamics Web API as
        // "_<attributename>_value", never as the bare FetchXML attribute name
        // — confirmed against a live response where "indskr_sampleproductid"
        // was requested but only "_indskr_sampleproductid_value" was present.
        skuId: row['_indskr_sampleproductid_value'] ?? '',
        limit,
        remaining: customerAllocationId ? (row['ca.indskr_totalsamplesremaining'] ?? 0) : limit,
        dropped: customerAllocationId ? (row['ca.indskr_totalsamplesdropped'] ?? 0) : 0,
        isUnlimited: customerAllocationId ? !!row['ca.indskr_isunlimited'] : !!row['indskr_isunlimited'],
        // indskr_startdate/enddate are Date-only fields ("2026-07-14", no time
        // component) — `new Date(...)` parses that as UTC midnight, which
        // lands hours into the PREVIOUS calendar day for any timezone behind
        // UTC and — worse — makes the END date expire at the very START of
        // its own day everywhere else, blocking a same-day config for
        // essentially its entire valid day. changeUTCDateToLocalDateWith0Time
        // is the same fix the Sample Drop feature already applies to this
        // exact field pair on the same entity (CustomerSampleAllocation) —
        // reused here for consistency: local midnight for the start boundary,
        // local 23:59:59 for the end boundary.
        startDate: row['indskr_startdate']
          ? Utility.changeUTCDateToLocalDateWith0Time(new Date(row['indskr_startdate']).getTime())
          : null,
        endDate: row['indskr_enddate']
          ? Utility.changeUTCDateToLocalDateWith0Time(new Date(row['indskr_enddate']).getTime(), true)
          : null,
        allocationConfigId: row['indskr_sampleproductdropconfigurationid'] ?? '',
        // "aa" is the outer join through the N:N intersect entity, filtered
        // to this contact — its contactid comes back populated only when the
        // association actually matched.
        isEligible: row['aa.contactid'] != null,
      };
    });
  }

  /**
   * Bulk sibling of fetchManualAllocationsForContact — one call per rep
   * position instead of one per contact. Returns a Map keyed by contactId
   * (lower-cased not required; Dynamics GUIDs come back consistently
   * cased) so the caller can merge straight into
   * SampleDeliveryRequestService.manualAllocationsByContact per contact.
   * A contact can appear via ca_contactid (consumption history), via
   * aa_contactid (eligible association with no consumption history yet),
   * both, or neither (config exists, this contact has no relationship to
   * it at all — that contact simply won't appear in the returned map for
   * this config row).
   */
  async fetchManualAllocationsForPosition(positionIds: string[], skuIds: string[]): Promise<Map<string, SdrCustomerAllocation[]>> {
    const result = new Map<string, SdrCustomerAllocation[]>();
    if (!positionIds?.length || !skuIds?.length) return result;

    let sampleProductIdsXml = '';
    skuIds.forEach((id) => { sampleProductIdsXml += `<value>${id}</value>`; });
    let positionIdsXml = '';
    positionIds.forEach((id) => { positionIdsXml += `<value>${id}</value>`; });
    const compareToDate = this.dateTimeFormatsService.formatDate(new Date(), 'YYYY-MM-DD');

    const configsFetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsActiveConfigsForSkus
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace('{compareToDate}', compareToDate);
    const consumptionFetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsConsumptionForPosition
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace(/\{positionIDs\}/g, positionIdsXml)
      .replace('{compareToDate}', compareToDate);
    const eligibilityFetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsEligibilityForPosition
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace(/\{positionIDs\}/g, positionIdsXml)
      .replace('{compareToDate}', compareToDate);

    // Paginate — a position/territory whose row count exceeds one Dynamics
    // page would otherwise be silently truncated after a single
    // executeFetchXml call, leaving contacts outside that page with no
    // confirmed allocation data and therefore unvalidated offline (OMNI-61497
    // root cause). Mirrors allocation-shipment.data.service.ts's own do/while
    // PagingInfo loop — the established pattern in this same
    // data-services/sample/ directory — rather than DynamicsWebApi's
    // executeFetchXmlAll, which isn't exposed by DynamicsClientService (the
    // wrapper every call in this file goes through) and would mean either
    // extending that shared wrapper or bypassing its httpGate semaphore
    // wrapping with a second raw client instance.
    const fetchAllPages = async (entitySet: string, fetchXml: string): Promise<any[]> => {
      // any: matches allocation-shipment.data.service.ts's own pagingInfo typing —
      // PagingInfo isn't a separately exported member of the DynamicsWebApi
      // namespace, only an inline shape on FetchXmlResponse itself.
      let pagingInfo: any = null;
      const rows: any[] = [];
      do {
        const response = await this.dynamics.executeFetchXml<any>(
          entitySet,
          fetchXml,
          undefined,
          pagingInfo?.nextPage ?? 0,
          pagingInfo?.cookie ?? null,
        );
        pagingInfo = response?.PagingInfo ?? null;
        if (Array.isArray(response?.value)) rows.push(...response.value);
      } while (pagingInfo !== null);
      return rows;
    };

    // Three separate, non-cross-joined queries run concurrently instead of one
    // query that cross-joined consumption and eligibility off the same config
    // row — see fetchManualAllocationsEligibilityForPosition's own FetchXML
    // comment for why that blew up to 35K+ rows and how each of these three
    // avoids it. configsFetchXml/eligibilityFetchXml are both rooted at
    // indskr_sampleproductdropconfiguration (ALLOCATION_CONFIG_ENTITY);
    // consumptionFetchXml is rooted at indskr_customersampleproduct instead
    // (CUSTOMER_SAMPLE_PRODUCT_ENTITY, same entity set
    // fetchDirtyManualAllocationContactIds already uses for that root) — each
    // FetchXML's root entity must match the entity SET passed to
    // executeFetchXml, not just any query in this file.
    const [configRows, consumptionRows, eligibilityRows] = await Promise.all([
      fetchAllPages(ALLOCATION_CONFIG_ENTITY, configsFetchXml),
      fetchAllPages(CUSTOMER_SAMPLE_PRODUCT_ENTITY, consumptionFetchXml),
      fetchAllPages(ALLOCATION_CONFIG_ENTITY, eligibilityFetchXml),
    ]);

    const configById = new Map<string, { skuId: string; limit: number; isUnlimited: boolean; startDate: Date | null; endDate: Date | null }>();
    for (const row of configRows) {
      const configId = row['indskr_sampleproductdropconfigurationid'];
      if (!configId) continue;
      configById.set(configId, {
        skuId: row['_indskr_sampleproductid_value'] ?? '',
        limit: row['indskr_eligibilitylimit'] ?? 0,
        isUnlimited: !!row['indskr_isunlimited'],
        // See the UTC/local date-window comment in fetchManualAllocationsForContact above.
        startDate: row['indskr_startdate']
          ? Utility.changeUTCDateToLocalDateWith0Time(new Date(row['indskr_startdate']).getTime())
          : null,
        endDate: row['indskr_enddate']
          ? Utility.changeUTCDateToLocalDateWith0Time(new Date(row['indskr_enddate']).getTime(), true)
          : null,
      });
    }

    // Consumption-history side: a contact who's actually requested/received
    // samples against a config.
    for (const row of consumptionRows) {
      const configId: string | undefined = row['ca_configid'] ?? undefined;
      const config = configId ? configById.get(configId) : undefined;
      const contactId: string | undefined = row['ca_contactid'] ?? undefined;
      if (!config || !contactId) continue;
      this._appendAllocation(result, contactId, {
        id: row['ca_allocationid'] ?? undefined,
        skuId: config.skuId,
        limit: config.limit,
        remaining: row['ca_remaining'] ?? 0,
        dropped: row['ca_dropped'] ?? 0,
        isUnlimited: !!row['ca_isunlimited'],
        startDate: config.startDate,
        endDate: config.endDate,
        allocationConfigId: configId,
        isEligible: true,
      });
    }

    // Eligibility side: everyone associated to a config via the N:N,
    // regardless of consumption history — a DIFFERENT contact set from the
    // consumption side above. Only add a row for a contact that didn't
    // already get one from the consumption side for this same SKU — a
    // contact with real consumption history is always eligible by
    // definition (see SdrCustomerAllocation.isEligible doc comment), so this
    // only fills in "eligible, first-ever request".
    for (const row of eligibilityRows) {
      const configId: string | undefined = row['aa_configid'] ?? undefined;
      const config = configId ? configById.get(configId) : undefined;
      const contactId: string | undefined = row['aa_contactid'] ?? undefined;
      if (!config || !contactId) continue;
      if (this._hasAllocationForSku(result, contactId, config.skuId)) continue;
      this._appendAllocation(result, contactId, {
        id: undefined,
        skuId: config.skuId,
        limit: config.limit,
        remaining: config.limit,
        dropped: 0,
        isUnlimited: config.isUnlimited,
        startDate: config.startDate,
        endDate: config.endDate,
        allocationConfigId: configId,
        isEligible: true,
      });
    }

    return result;
  }

  private _appendAllocation(map: Map<string, SdrCustomerAllocation[]>, contactId: string, allocation: SdrCustomerAllocation): void {
    const existing = map.get(contactId);
    if (existing) existing.push(allocation);
    else map.set(contactId, [allocation]);
  }

  private _hasAllocationForSku(map: Map<string, SdrCustomerAllocation[]>, contactId: string, skuId: string): boolean {
    return (map.get(contactId) ?? []).some((a) => a.skuId === skuId);
  }

  /**
   * Whether [[fetchManualAllocationsForPosition]]'s bulk, territory-wide
   * FetchXML should run this pass. That query fans out two INDEPENDENT
   * one-to-many joins (consumption history "ca", eligibility association
   * "aa") off the same config row — FetchXML cross-joins those, so a config
   * with 500 eligible contacts and 70 with purchase history returns up to
   * 500*70 rows for that one config alone (confirmed root cause of the 35K+
   * row counts seen in practice, not 35K distinct facts). That multiplication
   * can't be fixed with a simple modifiedon filter on this query, so the full
   * bulk pull only runs on the very first sync ever (no persisted
   * lastUpdatedTime) or when forceFullSync is true. Every other (delta) pass
   * uses [[fetchManualAllocationsDirtyContactsForPosition]] instead — see
   * that FetchXML's own comment for why it doesn't have the same fan-out.
   */
  async shouldRunFullPositionAllocationSync(forceFullSync: boolean): Promise<boolean> {
    if (forceFullSync) return true;
    const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_SDR_MANUAL_ALLOCATIONS_POSITION);
    return !syncState?.lastUpdatedTime;
  }

  /**
   * The timestamp [[fetchDirtyManualAllocationContactIds]]'s delta pass
   * should search from — the later of the last delta check and the last full
   * sync (both fields live on the same sync-state doc). `null` means no full
   * sync has ever completed, so the delta path has nothing to anchor to yet
   * (the caller should be running a full sync instead in that case).
   */
  async getPositionAllocationDeltaSinceTime(): Promise<number | null> {
    const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_SDR_MANUAL_ALLOCATIONS_POSITION);
    return syncState?.lastDeltaCheckedAt ?? syncState?.lastUpdatedTime ?? null;
  }

  /**
   * Marks a successful FULL sync — resets both the full-sync and delta-check
   * cursors to now. The delta-check cursor doubles as the "since" bound for
   * BOTH the consumption-dirty check and the config-eligibility-dirty check
   * (same cadence, one shared cursor — see markPositionAllocationDeltaChecked).
   */
  async markFullPositionAllocationSyncComplete(): Promise<void> {
    const now = Date.now();
    await this.disk.updateSyncState(
      { _id: DB_SYNC_STATE_KEYS.SYNC_SDR_MANUAL_ALLOCATIONS_POSITION, lastUpdatedTime: now, lastDeltaCheckedAt: now },
      true,
    );
  }

  /**
   * Marks a successful DELTA pass — advances the shared delta cursor, keeping
   * lastUpdatedTime (the full-sync marker) untouched. Shared by both
   * [[fetchDirtyManualAllocationContactIds]] (consumption) and
   * [[fetchDirtyManualAllocationConfigIds]] (config eligibility) — call this
   * ONCE per delta pass, only after BOTH checks have completed, not once per
   * check. Advancing it after only one has run would make the other silently
   * skip whatever window it hadn't actually processed yet on its next read.
   */
  async markPositionAllocationDeltaChecked(): Promise<void> {
    const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_SDR_MANUAL_ALLOCATIONS_POSITION);
    await this.disk.updateSyncState(
      { ...(syncState ?? {}), _id: DB_SYNC_STATE_KEYS.SYNC_SDR_MANUAL_ALLOCATIONS_POSITION, lastDeltaCheckedAt: Date.now() },
      true,
    );
  }

  /**
   * Delta sibling of [[fetchManualAllocationsForPosition]] — returns the
   * deduped list of contactIds whose Manual-Distribution Customer Allocation
   * changed on/after `sinceEpochMs`, scoped to the rep's position(s) and
   * tracked SKUs. See the FetchXML's own comment (dynamics-fetchQueries.ts)
   * for why this avoids the bulk query's row-count blow-up. The caller is
   * expected to refresh each returned contactId via the already-correct,
   * already-safe [[fetchManualAllocationsForContact]] (single-contact scope,
   * no cross-join risk) rather than trying to assemble a full allocation
   * record from this query's minimal row shape.
   */
  async fetchDirtyManualAllocationContactIds(positionIds: string[], skuIds: string[], sinceEpochMs: number): Promise<string[]> {
    if (!positionIds?.length || !skuIds?.length) return [];

    let sampleProductIdsXml = '';
    skuIds.forEach((id) => { sampleProductIdsXml += `<value>${id}</value>`; });
    let positionIdsXml = '';
    positionIds.forEach((id) => { positionIdsXml += `<value>${id}</value>`; });
    const sinceDateTime = new Date(sinceEpochMs).toISOString();

    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsDirtyContactsForPosition
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace(/\{positionIDs\}/g, positionIdsXml)
      .replace('{sinceDateTime}', sinceDateTime);

    // Same paging rationale as fetchManualAllocationsForPosition — a large
    // enough delta batch could still exceed one page.
    let pagingInfo: any = null;
    const rows: any[] = [];
    do {
      const response = await this.dynamics.executeFetchXml<any>(
        CUSTOMER_SAMPLE_PRODUCT_ENTITY,
        fetchXml,
        undefined,
        pagingInfo?.nextPage ?? 0,
        pagingInfo?.cookie ?? null,
      );
      pagingInfo = response?.PagingInfo ?? null;
      if (Array.isArray(response?.value)) rows.push(...response.value);
    } while (pagingInfo !== null);

    const contactIds = new Set<string>();
    for (const row of rows) {
      const contactId = row['dirty_contactid'];
      if (contactId) contactIds.add(contactId);
    }
    return Array.from(contactIds);
  }

  /**
   * Eligibility-association delta check, step 1 of 2 — returns the deduped
   * list of config ids whose OWN modifiedon changed on/after `sinceEpochMs`
   * (i.e. their "aa" eligibility association gained or lost a contact since
   * then; associating/disassociating bumps the parent config's modifiedon
   * even though the association row itself has none — confirmed live).
   * Configs aren't position-scoped, so no positionIds param here. The caller
   * is expected to run [[fetchEligibleContactIdsForConfigs]] for whatever
   * (usually empty) set this returns — this method alone cannot tell you
   * which contact within a changed config's list is the new one, only which
   * configs need that follow-up read at all.
   */
  async fetchDirtyManualAllocationConfigIds(skuIds: string[], sinceEpochMs: number): Promise<string[]> {
    if (!skuIds?.length) return [];

    let sampleProductIdsXml = '';
    skuIds.forEach((id) => { sampleProductIdsXml += `<value>${id}</value>`; });
    const sinceDateTime = new Date(sinceEpochMs).toISOString();
    const compareToDate = this.dateTimeFormatsService.formatDate(new Date(), 'YYYY-MM-DD');

    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchDirtyManualAllocationConfigIds
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace('{sinceDateTime}', sinceDateTime)
      .replace('{compareToDate}', compareToDate);

    // No link-entities at all here (configs aren't position-scoped), but
    // still paginate defensively — a rep tracking many SKUs could still
    // exceed one page of matching configs.
    let pagingInfo: any = null;
    const rows: any[] = [];
    do {
      const response = await this.dynamics.executeFetchXml<any>(
        ALLOCATION_CONFIG_ENTITY,
        fetchXml,
        undefined,
        pagingInfo?.nextPage ?? 0,
        pagingInfo?.cookie ?? null,
      );
      pagingInfo = response?.PagingInfo ?? null;
      if (Array.isArray(response?.value)) rows.push(...response.value);
    } while (pagingInfo !== null);

    const configIds = new Set<string>();
    for (const row of rows) {
      const configId = row['indskr_sampleproductdropconfigurationid'];
      if (configId) configIds.add(configId);
    }
    return Array.from(configIds);
  }

  /**
   * Eligibility-association delta check, step 2 of 2 — returns the FULL,
   * deduped set of contactIds currently eligible (via the "aa" N:N
   * association) for ONLY the given config ids (the output of
   * [[fetchDirtyManualAllocationConfigIds]]), scoped to the rep's
   * position(s). Full, not incremental, because the association row itself
   * has no timestamp to narrow further — see that method's own comment. The
   * caller (SampleDeliveryRequestService.syncManualAllocationsForPosition)
   * is expected to diff this against its own already-cached contacts to
   * find who's newly eligible, then refresh only those via
   * [[fetchManualAllocationsForContact]] — this method itself does no
   * diffing, it's a plain scoped read.
   */
  async fetchEligibleContactIdsForConfigs(configIds: string[], positionIds: string[], skuIds: string[]): Promise<string[]> {
    if (!configIds?.length || !positionIds?.length || !skuIds?.length) return [];

    let sampleProductIdsXml = '';
    skuIds.forEach((id) => { sampleProductIdsXml += `<value>${id}</value>`; });
    let positionIdsXml = '';
    positionIds.forEach((id) => { positionIdsXml += `<value>${id}</value>`; });
    let configIdsXml = '';
    configIds.forEach((id) => { configIdsXml += `<value>${id}</value>`; });
    const compareToDate = this.dateTimeFormatsService.formatDate(new Date(), 'YYYY-MM-DD');

    const fetchXml = fetchQueries.sampleDeliveryRequest.fetchManualAllocationsEligibleContactsForConfigs
      .replace('{sampleProductIds}', sampleProductIdsXml)
      .replace('{configIds}', configIdsXml)
      .replace(/\{positionIDs\}/g, positionIdsXml)
      .replace('{compareToDate}', compareToDate);

    // Same paging rationale as fetchManualAllocationsForPosition — scoped to
    // a handful of changed configs in practice, but still paginate
    // defensively rather than assume it always fits one page.
    let pagingInfo: any = null;
    const rows: any[] = [];
    do {
      const response = await this.dynamics.executeFetchXml<any>(
        ALLOCATION_CONFIG_ENTITY,
        fetchXml,
        undefined,
        pagingInfo?.nextPage ?? 0,
        pagingInfo?.cookie ?? null,
      );
      pagingInfo = response?.PagingInfo ?? null;
      if (Array.isArray(response?.value)) rows.push(...response.value);
    } while (pagingInfo !== null);

    const contactIds = new Set<string>();
    for (const row of rows) {
      const contactId = row['eligible_contactid'];
      if (contactId) contactIds.add(contactId);
    }
    return Array.from(contactIds);
  }

  /**
   * Persist one contact's Manual-Distribution allocation records as a
   * last-synced snapshot — read back by [[loadManualAllocationsFromDb]] when
   * a live fetch isn't possible (offline, or the live fetch itself failed).
   * Write-through: called right after a successful [[fetchManualAllocationsForContact]].
   */
  async saveManualAllocationsToDb(contactId: string, allocations: SdrCustomerAllocation[]): Promise<void> {
    if (!contactId) return;
    await this.disk.updateOrInsert(DB_KEY_PREFIXES.SDR_MANUAL_ALLOC + contactId, (doc: any) => {
      if (!doc || !doc.raw) doc = { raw: [] };
      doc.raw = allocations;
      return doc;
    });
  }

  /**
   * Batched equivalent of [[saveManualAllocationsToDb]] for the full position sync
   * (syncManualAllocationsForPosition), which can cover hundreds-to-thousands of
   * contacts in one pass. Calling saveManualAllocationsToDb per contact there meant
   * one fully serial get->decrypt->encrypt->put round-trip per contact - the dominant
   * cost of that sync stage. This fetches every target doc's current _rev in one
   * allDocs call (existing contacts only; new ones simply have none), then writes
   * everything via disk.bulkInChunks instead.
   */
  async saveManualAllocationsToDbBulk(byContact: Map<string, SdrCustomerAllocation[]>): Promise<void> {
    if (!byContact.size) return;
    const idFor = (contactId: string) => DB_KEY_PREFIXES.SDR_MANUAL_ALLOC + contactId;
    const ids = Array.from(byContact.keys()).map(idFor);
    const existing: any = await this.disk.batchFetch({ keys: ids, include_docs: false }, false, true);
    const revById = new Map<string, string>();
    for (const row of existing?.rows ?? []) {
      if (row?.id && row?.value?.rev && !row.value.deleted) {
        revById.set(row.id, row.value.rev);
      }
    }
    const docs = Array.from(byContact.entries()).map(([contactId, allocations]) => {
      const _id = idFor(contactId);
      const doc: any = { _id, raw: allocations };
      const rev = revById.get(_id);
      if (rev) doc._rev = rev;
      return doc;
    });
    await this.disk.bulkInChunks(docs, 200);
  }

  /**
   * Read a contact's last-synced Manual-Distribution allocation snapshot
   * ([] if none cached yet), rehydrating startDate/endDate back to Date
   * instances (PouchDB round-trips them to strings).
   */
  async loadManualAllocationsFromDb(contactId: string): Promise<SdrCustomerAllocation[]> {
    if (!contactId) return [];
    const doc = await this.disk.retrieve(DB_KEY_PREFIXES.SDR_MANUAL_ALLOC + contactId, true);
    const raw = doc?.raw;
    if (!Array.isArray(raw)) return [];
    return raw.map((o: any) => ({
      ...o,
      startDate: o?.startDate ? new Date(o.startDate) : null,
      endDate: o?.endDate ? new Date(o.endDate) : null,
    }));
  }

}
